import ConfigurationOptions from '../layouts/ConfigurationOptions.jsx';
import ConfigurationTableSearch, { parameterTableRows, matchesParameterName } from './ConfigurationTableSearch';
import ClassifierFields, { classifierSettings, classifierError, classifierOutputOptions } from './ClassifierFields';
import React, { useState, useEffect, useRef } from 'react';
import server from '../../api/server.js';
import "../loader.css"
import { X, Info, Archive, Wand2, Save, Upload, Download } from 'lucide-react';
import { FaRegEdit, FaTimes } from "react-icons/fa";
import ExpandableParametersComponent from '../layouts/agentFeedbackLayout.jsx'
import ModernTimeDropdown from '../layouts/modernDropdown.jsx';
import { useToast } from '../../context/ToastContext.jsx';
import SampleTestModal from '../layouts/SampleTestModal.jsx'
import { IoChatbubbleOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { TbRestore } from "react-icons/tb";
import { Sparkles } from 'lucide-react';
import { GrSwitch } from 'react-icons/gr';
import { BiDownArrow, BiUpArrow } from 'react-icons/bi';
import FeedbackSection from '../layouts/FeedbackSection.jsx';
import DeployConfirmationModal from '../layouts/DeployConfirmationModal.jsx';
import { getApiToastMessage } from '../../utils/apiToastMessage.js';
import { findBoundarySpaceName, hasBoundarySpace, getBoundarySpaceNameError } from '../../utils/moduleNameValidation.js';
import ConfigurationDiffButton from '../layouts/ConfigurationDiffButton.jsx';

const callTypeOptions = [
  ['run_for_non_meaningful_calls', 'Non-Meaningful calls'],
  ['run_for_not_connected_calls', 'Not Connected calls'],
];
const callTypeSettings = (value = {}) => Object.fromEntries(
  callTypeOptions.filter(([key]) => value[key] !== undefined).map(([key]) => [key, value[key]])
);

const moduleCallTypeSettings = (value = {}) => Object.fromEntries(
  callTypeOptions.map(([key]) => [key, value[key] ?? 0])
);

const configWithExecutionSettings = (configuration, settings) => ({
  configuration,
  ...moduleCallTypeSettings(settings),
});

const AgentFeedbackConfigure = ({clientID, serviceID, module_name, setCloseModal, config_data}) => {
  const [moduleExecution, setModuleExecution] = useState(moduleCallTypeSettings());
  const [loadedModuleExecution, setLoadedModuleExecution] = useState(moduleCallTypeSettings());
  const [parameterExecution, setParameterExecution] = useState({});
  const [tableSearch, setTableSearch] = useState({});
  const roundToTwo = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  const parseWeightage = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    const bounded = Math.min(100, Math.max(0, numeric));
    return roundToTwo(bounded);
  };
  const sanitizeWeightageInput = (rawValue) => {
    const value = String(rawValue ?? "").trim();
    if (value === "") return "";
    if (!/^\d{0,3}(\.\d{0,2})?$/.test(value)) return null;
    const numeric = Number(value);
    if (Number.isNaN(numeric) || numeric < 0 || numeric > 100) return null;
    return value;
  };
  const getTotalWeightage = (params = []) =>
    roundToTwo(params.reduce((sum, param) => sum + parseWeightage(param?.weightage), 0));
  const isTotalWeightageValid = (params = []) => Math.abs(getTotalWeightage(params) - 100) < 0.0001;

  const [showPreview, setShowPreview] = useState(false);
  const [expandedSample, setExpandedSample] = useState(false)
  const [activeTab, setActiveTab] = useState('individual');
  const [subparameters, setSubparameters] = useState([])
  const [isValid, setIsValid] = useState(false)
  const { showToast } = useToast();
  const [parameters, setParameters] = useState([]);
  const [agentFeedbackModule, setAgentFeedbackModule] = useState(null)
  const [servicetab, setServicetab] = useState('Current')
  const [criteria, setCriteria] = useState(null)
  const [selectedSubparameter, setSelectedSubparameter] = useState('');
  const [ usingSubParamError, setUsingSubparamError] = useState(null)
  const [defaultActive, setDefaultActive] = useState(false)
  const [showEditInfo, setShowEditInfo] = useState(false)
  const [moduleDraftSaveConfiguration, setModuleDraftSaveConfiguration] = useState(null)
  const [configType, setConfigType] = useState('Live Version')
  const [showConfigDropdown, setShowConfigDropdown] = useState(false)
  const [showSampleTest, setShowSampleTest] = useState(false)
  const [showSampleTestModal, setShowSampleTestModal] = useState(false)
  const [showArchivedParameters, setShowArchivedParameters] = useState(false);
  const [showArchivedSubparameters, setShowArchivedSubparameters] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveItem, setArchiveItem] = useState(null);
  const [archiveType, setArchiveType] = useState(null); // 'parameter' or 'subparameter'
  const audioRef = useRef()
  const segmentRefs = useRef([]);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null);
  const [promptQuery, setPromptQuery] = useState('')
  const [logicExplanation, setLogicExplanation] = useState('')
  const [writeWithAiEnabled, setWriteWithAiEnabled] = useState(false)
  const [logicLoading, setLogicLoading] = useState(false)
  const [loading, setLoading] = useState(false);
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false);
  const [current_version, setCurrent_version] = useState(0)
  const [showMoreMap, setShowMoreMap] = useState({})
  const sourceClickInProgressRef = useRef(false);
  const [fatal_zero_marking, setFatal_zero_marking] = useState(false)
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  const [descriptionError, setDescriptionError] = useState(null)
  const [showLoadConfigurePart, setShowLoadConfigurePart] = useState(false)
  // Training Materials state
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [showTrainingMaterialForm, setShowTrainingMaterialForm] = useState(false);
  const [editingTrainingIndex, setEditingTrainingIndex] = useState(null);
  const [selectedTrainingParamDisplay, setSelectedTrainingParamDisplay] = useState('');
  const [selectedTrainingParamKey, setSelectedTrainingParamKey] = useState('');
  const [materialRows, setMaterialRows] = useState([]);
  const trainingFormRef = useRef(null);

  const checkDefinitionValidity = async (definition, type, options) => {
    if(type === 'Categorical - Single Value' || type === 'Categorical - Multiple Values' || type === 'Yes/No/NA' || type === 'Number' || type === 'Date and Time'){
      let res = await server.checkDefinitionValidity(client_id, service_id, email, 'apps', undefined, definition, type, options, 'agent_feedback')
      let resp = await res.json()
      if(res.status === 200){
        if(resp.data){
          if(resp.data.error){
            return {error: resp.data.error}
          }else{
            return {error: null}
          }
        }
      }else{
        return {error: resp.error}
      }
    }
  }
  
  // Upload and Download Configuration Functions
  /** Normalize status for export so archived/inactive always appear (handles legacy `archived: true` without `status`). */
  const statusForConfigExport = (item) => {
    const s = item?.status;
    if (s === 'archived' || s === 'inactive' || s === 'active') return s;
    if (item?.archived === true) return 'archived';
    return s ?? 'active';
  };

  const handleDownloadConfig = () => {
    try {
      const configData = {
        criteria: {
          minimum_call_duration: parseInt(criteria?.minimum_call_duration) || 0,
          audio_quality_threshold: criteria?.audio_quality_threshold || 'Both parties interacted atleast once.',
          count_total_conversational_messages: parseInt(criteria?.count_total_conversational_messages || 0),
          count_user_conversational_messages: parseInt(criteria?.count_user_conversational_messages || 0)
        },
        parameters: parameters.map(param => ({
          display_name: param.display_name,
          fatal: param.fatal || 0,
          logic: param.logic || '',
          weightage: parseWeightage(param.weightage),
          subparameters_used: param.subparameters_used || [],
          output_type: 'Number',
          threshold_score: parseInt(param.threshold_score) || 0,
          status: statusForConfigExport(param)
        })),
        subparameters: subparameters.map(subparam => {
          const base = {
            display_name: subparam.display_name,
            output_type: subparam.output_type || 'Yes/No',
            system: subparam.system || 0,
            possible_values: subparam.possible_values || [],
            status: statusForConfigExport(subparam),
            ...classifierSettings(subparam),
            subparameter_type: subparam.subparameter_type || 'llm_based',
          };
          if (subparam.subparameter_type === 'logic_based') {
            base.logic = subparam.logic || '';
            base.subparameters_used = subparam.subparameters_used || [];
            base.execution_order = subparam.execution_order || 0;
          } else {
            base.instruction = subparam.instruction || '';
            base.context_scope = subparam.context_scope || 'conversation';
            Object.assign(base, callTypeSettings(subparam));
          }
          return base;
        }),
        training_materials: serializeTrainingMaterials(),
        fatal_zero_marking: fatal_zero_marking,
        ...moduleExecution, moduleActive: moduleActive
      };

      const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent_feedback_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Configuration downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading configuration:', error);
      showToast('Error downloading configuration', 'error');
    }
  };

  const handleUploadConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const configData = JSON.parse(e.target.result);
        setLoading(true);
        const boundarySpaceError =
          findBoundarySpaceName(configData.parameters || [], 'display_name', 'Parameter name') ||
          findBoundarySpaceName(configData.subparameters || [], 'display_name', 'Subparameter name');
        if (boundarySpaceError) {
          showToast(boundarySpaceError, 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        const validationConfig = {
          configuration: {
            criteria: {
              minimum_call_duration: parseInt(configData.criteria?.minimum_call_duration) || 0,
              audio_quality_threshold: configData.criteria?.audio_quality_threshold || 'Both parties interacted atleast once.',
              count_total_conversational_messages: parseInt(configData.criteria?.count_total_conversational_messages || 0),
              count_user_conversational_messages: parseInt(configData.criteria?.count_user_conversational_messages || 0)
            },
            parameters: (configData.parameters || []).reduce((acc, param) => {
              acc[param.display_name.toLowerCase().replace(/\s+/g, '_')] = {
                fatal: param.fatal || 0,
                logic: param.logic || '',
                weightage: parseWeightage(param.weightage),
                display_name: param.display_name,
                subparameters_used: param.subparameters_used || [],
                threshold_score: parseInt(param.threshold_score) || 0,
                output_type: 'Number',
                logic_explanation: param.logic_explanation || '',
                status: param.status || (param.archived === true ? 'archived' : 'active')
              };
              return acc;
            }, {}),
            subparameters: serializeSubparameters(configData.subparameters || []),
            training_materials: configData.training_materials || [],
            fatal_zero_marking: configData.fatal_zero_marking === true ? 1 : (parseInt(configData.fatal_zero_marking) || 0),
          },
          ...moduleCallTypeSettings(configData), active: configData.moduleActive === true ? 1 : 0
        };
        const validationResp = await server.validateModuleJsonConfiguration({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'agent_feedback', configuration: validationConfig});
        if (validationResp.status !== 200) {
          showToast(await getApiToastMessage(validationResp, 'Invalid configuration'), 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        
        // Validate the uploaded configuration
        if (!configData.criteria || !configData.parameters || !configData.subparameters) {
          throw new Error('Invalid configuration format');
        }
        
        if (configData.subparameters) {
          const normalizedSubparameters = configData.subparameters.map((subparam, index) => {
            const status =
              subparam.status ||
              (subparam.archived === true ? 'archived' : 'active');
            const { archived: _legacyArchived, ...rest } = subparam;
            return {
              ...rest,
              id: index + 1,
              status,
            };
          });
          setSubparameters(normalizedSubparameters);
        }

        // Update the state with uploaded configuration
        if (configData.criteria) {
          setCriteria(configData.criteria);
        }
        
        if (configData.parameters) {
          const normalizedParameters = configData.parameters.map((param, index) => {
            const status =
              param.status ||
              (param.archived === true ? 'archived' : 'active');
            const { archived: _legacyArchived, ...rest } = param;
            return {
              ...rest,
              id: index + 1,
              status,
            };
          });
          setParameters(normalizedParameters);
        }
        
        if (configData.fatal_zero_marking !== undefined) {
          setFatal_zero_marking(configData.fatal_zero_marking);
        }
        
        if (configData.moduleActive !== undefined) {
          setModuleActive(configData.moduleActive);
        }
        setModuleExecution(moduleCallTypeSettings(configData));

        showToast(await getApiToastMessage(validationResp, 'Configuration uploaded successfully!'), 'success');
        setLoading(false);
      } catch (error) {
        console.error('Error parsing uploaded configuration:', error);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
        setLoading(false);
      }
    };
    
    reader.readAsText(file);
  };

  const convertToSeconds = (time) => {
    if(typeof time === 'string'){
      const [hours, minutes, seconds] = time.split(':').map(Number);
      return (hours * 3600) + (minutes * 60) + seconds;
    }
    return time;
  }
  const handleSourceClick = (source, segments) => {
    let start, end, message_id;
    if(source.start_timestamp && source.end_timestamp){
      // Set flag to prevent automatic segment selection
      sourceClickInProgressRef.current = true;
      
      // Convert timestamps to seconds with proper validation
      try {
        if(typeof source.start_timestamp === 'string'){
          start = convertToSeconds(source.start_timestamp)-1;
        }else{
          start = parseFloat(source.start_timestamp)-1;
        }
        if(typeof source.end_timestamp === 'string'){
          end = convertToSeconds(source.end_timestamp)+1;
        }else{
          end = parseFloat(source.end_timestamp)+1;
        }
        
        // Validate that start and end are valid numbers
        if (isNaN(start) || isNaN(end)) {
          console.warn('Invalid timestamp values:', source.start_timestamp, source.end_timestamp);
          sourceClickInProgressRef.current = false;
          return;
        }
        
        // Ensure start time is always less than end time
        if (start >= end) {
          console.warn('Start time must be less than end time:', start, end);
          sourceClickInProgressRef.current = false;
          return;
        }
        
      } catch (error) {
        console.warn('Error converting timestamps:', error);
        sourceClickInProgressRef.current = false;
        return;
      }
      
      // Handle audio case
      // Check if audio element exists and is valid
      if (!audioRef.current) {
        console.warn('Audio element not available');
        sourceClickInProgressRef.current = false;
        return;
      }

      try {
        // Pause any currently playing audio first
        audioRef.current.pause();
        
        // Remove any existing timeupdate listeners
        if (audioRef.current._currentTimeUpdateHandler) {
          audioRef.current.removeEventListener('timeupdate', audioRef.current._currentTimeUpdateHandler);
          audioRef.current._currentTimeUpdateHandler = null;
        }
        
        // Create new timeupdate handler
        const timeUpdateHandler = () => {
          if (!audioRef.current) return;
          
          const currentTime = audioRef.current.currentTime;
          
          if (currentTime >= end) {
            audioRef.current.pause();
            audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
            audioRef.current._currentTimeUpdateHandler = null;
          }
        };
        
        // Store reference to handler for cleanup
        audioRef.current._currentTimeUpdateHandler = timeUpdateHandler;
        
        // Add the new listener
        audioRef.current.addEventListener('timeupdate', timeUpdateHandler);
        
        // Seek to start time
        audioRef.current.currentTime = start;
        
        // Play with proper error handling
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Find the segment index whose range includes the start time
              let idx = 0;
              for (let i = 0; i < segments.length; i++) {
                if (start >= segments[i].start_time && start <= segments[i].end_time) {
                  idx = i;
                  break;
                }
              }
              setSelectedSegmentIdx(idx);
              
              // Scroll the segment into view
              if (segmentRefs.current[idx] && idx === selectedSegmentIdx) {
                segmentRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
              
              // Clear the flag after successful playback start
              sourceClickInProgressRef.current = false;
            })
            .catch(error => {
              console.warn('Error playing audio:', error);
              // Clean up listener on error
              audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
              audioRef.current._currentTimeUpdateHandler = null;
              // Clear the flag on error
              sourceClickInProgressRef.current = false;
            });
        }
      } catch (error) {
        console.warn('Error handling source click:', error);
        sourceClickInProgressRef.current = false;
      }
    } else {
      // Handle chat message case
      message_id = source.segment_id;
      if(message_id){
        const message = segments.find(message => message.message_id === message_id);
        if(message){
          // Find the index of the message in chatTranscriptions
          const messageIndex = segments.findIndex(msg => msg.message_id === message_id);
          if (messageIndex !== -1) {
            // Create a ref for the message element if it doesn't exist
            if (!segmentRefs.current[messageIndex]) {
              segmentRefs.current[messageIndex] = React.createRef();
            }
            
            // Scroll the message into view
            setTimeout(() => {
              const messageElement = document.querySelector(`[data-message-id="${message_id}"]`);
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
                // Add a temporary highlight effect
                messageElement.style.backgroundColor = "rgba(111, 111, 254, 0.1)";
                setTimeout(() => {
                  messageElement.style.backgroundColor = "";
                }, 2000);
              }
            }, 100);
          }
        }
      }
    }
  }
  const toggleShowMore = (key) => {
    setShowMoreMap(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const scrollToSegment = (idx) => {
    const el = segmentRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };  
  const toggleSegment = (item, idx, link) => {
    setSelectedSegmentIdx(idx);
    scrollToSegment(idx);
    if(!link){
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = item.start_time; // Seek to the start time of the segment
      audioRef.current.play(); // Play the audio
    }
  };
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  function formatEpochTime(epochMillis) {
    if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) {
      return ""
    }
  
    const date = new Date(epochMillis);
    return date.toLocaleString(); // e.g., "5/29/2025, 2:30:45 PM"
  }
  const handleAudioTimeUpdate = (segments) => {
    if (sourceClickInProgressRef.current) return;
    const currentTime = audioRef.current.currentTime;

    // Find the segment corresponding to the current audio time
    segments.forEach((segment, idx) => {
      if (currentTime >= segment.start_time && currentTime <= segment.end_time) {
        if (segmentRefs.current[idx]) {
          // Scroll the corresponding segment into view if it's not already visible
          segmentRefs.current[idx].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });

          // Highlight the selected segment
          setSelectedSegmentIdx(idx);
        }
      }
    });
  };
  const getBorderColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"4px solid #6f6ffe"
      case 'agent':
        return"4px solid #3B82F6"
      case 'bot':
        return"4px solid gray"
      case 'system':
        return "4px solid gray"
      default:
        break;
    }
  }
  const getBackgroundColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"rgb(249, 250, 251)"
      case 'agent':
        return"rgb(249, 250, 251)"
      case 'bot':
        return"rgb(249, 250, 251)"
      case 'system':
        return"rgb(249, 250, 251)"
      default:
        break;
    }
  }
  const handleAddSubparameterToQuery = () => {
    if (selectedSubparameter && !selectedSubparameters.includes(selectedSubparameter) && allowedSubparameters.includes(selectedSubparameter)) {
      setSelectedSubparameters([...selectedSubparameters, selectedSubparameter]);
      setSelectedSubparameter('');
      setUsingSubparamError(null);
    }else{
      setUsingSubparamError('You can select subparameter from the list or add more subparameters.')
    }
  };
  const handleRemoveSubparameterFromQuery = (subparam) => {
    setSelectedSubparameters(selectedSubparameters.filter(param => param !== subparam));
  };
  const handleCloseSampleTestModal = () => {
    setShowSampleTestModal(false)
  }
  
  // Custom Tooltip Component
  const CustomTooltip = ({ text, children, id, position = "top" }) => {
    return (
      <div 
        className="relative inline-block"
        onMouseEnter={() => setTooltipVisible(id)}
        onMouseLeave={() => setTooltipVisible(null)}
      >
        {children}
        {tooltipVisible === id && (
          <div className={`absolute px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap z-50 ${
            position === "left" 
              ? "right-full top-1/2 transform -translate-y-1/2 mr-2" 
              : "bottom-full left-1/2 transform -translate-x-1/2 mb-2"
          }`}>
            {text}
            {position === "left" ? (
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-2 border-transparent border-l-gray-800"></div>
            ) : (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-2 border-transparent border-t-gray-800"></div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filter parameters based on status and showArchivedParameters setting
  const getFilteredParameters = () => {
    if (showArchivedParameters) {
      return parameters.filter(param => param.status === 'archived');
    } else {
      return parameters.filter(param => 
        !param.status || param.status === 'active' || param.status === 'inactive'
      );
    }
  };

  // Filter subparameters based on status and showArchivedSubparameters setting
  const getFilteredSubparameters = () => {
    if (showArchivedSubparameters) {
      return subparameters.filter(subparam => subparam.status === 'archived');
    } else {
      return subparameters.filter(subparam => 
        !subparam.status || subparam.status === 'active' || subparam.status === 'inactive'
      );
    }
  };

  // Check if parameter has weight allocation
  const hasWeightAllocation = (paramId) => {
    const param = parameters.find(p => p.id === paramId);
    return param && parseWeightage(param.weightage) > 0;
  };

  // Archive parameter with weight check
  const handleArchiveParameter = (parameter) => {
    if (hasWeightAllocation(parameter.id) || parameter.fatal) {
      showToast('Please set parameter weightage to 0 and remove from fatal condition before archiving', 'error');
      return;
    }
    setParameters(parameters.map(p => 
      p.id === parameter.id ? {...p, status: 'archived'} : p
    ));
  };

  // Archive subparameter with usage check
  const handleArchiveSubparameter = (subparameter) => {
    const { usedByParameters, usedByLogicSubparams } = getSubparameterArchiveBlockers(subparameter);
    if (usedByParameters.length > 0) {
      showToast(
        "Can't archive: this subparameter is used in parameters: " +
          usedByParameters.map((p) => p.display_name).join(', '),
        'error'
      );
      return;
    }
    if (usedByLogicSubparams.length > 0) {
      showToast(
        "Can't archive: this subparameter is referenced in the logic of: " +
          usedByLogicSubparams.map((s) => s.display_name).join(', '),
        'error'
      );
      return;
    }
    setSubparameters(subparameters.map(s => 
      s.id === subparameter.id ? {...s, status: 'archived'} : s
    ));
  };

  // Restore archived parameter back to active status
  const handleRestoreParameter = (parameter) => {
    setParameters(parameters.map(p => 
      p.id === parameter.id ? {...p, status: 'active'} : p
    ));
  };

  // Restore archived subparameter back to active status
  const handleRestoreSubparameter = (subparameter) => {
    setSubparameters(subparameters.map(s => 
      s.id === subparameter.id ? {...s, status: 'active'} : s
    ));
  };

  // Toggle parameter status with weight check
  const handleToggleParameterStatus = (parameter) => {
    if (parameter.status === 'active' && (hasWeightAllocation(parameter.id) || parameter.fatal)) {
      showToast('Please set parameter weightage to 0 and remove from fatal condition before making it inactive', 'error');
      return;
    }
    setParameters(parameters.map(p => 
      p.id === parameter.id ? {
        ...p, 
        status: p.status === 'active' ? 'inactive' : 'active'
      } : p
    ));
  };

  // Toggle subparameter status with usage check
  const handleToggleSubparameterStatus = (subparameter) => {
    if (subparameter.status === 'active' && isSubparameterUsed(subparameter.id)) {
      const { usedByParameters, usedByLogicSubparams } = getSubparameterArchiveBlockers(subparameter);
      const parts = [];
      if (usedByParameters.length > 0) {
        parts.push('parameters: ' + usedByParameters.map((p) => p.display_name).join(', '));
      }
      if (usedByLogicSubparams.length > 0) {
        parts.push(
          'logic-based subparameters: ' + usedByLogicSubparams.map((s) => s.display_name).join(', ')
        );
      }
      showToast("Can't deactivate — used in " + parts.join('; '), 'error');
      return;
    }
    setSubparameters(subparameters.map(s => 
      s.id === subparameter.id ? {
        ...s, 
        status: s.status === 'active' ? 'inactive' : 'active'
      } : s
    ));
  };
  const generateLogicWithAI = async () => {
    try {
      setLogicLoading(true)
      let subparams = subparameters.filter(subparam => selectedSubparameters.includes(toVariableName(subparam.display_name)))
      let res = await server.generateLogicWithAI(client_id, service_id, 'apps', undefined, email, promptQuery, JSON.stringify(subparams))
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data && resp.data.logic){
          setManualQuery(resp.data.logic)
          await verifyQuery(resp.data.logic)
        }
      } else {
        setLogicLoading(false)
        let errorResp = await res.json();
        showToast('Failed to generate logic. Please try again later.', 'error');
      }
    } catch (error) {
      setLogicLoading(false)
      showToast('Failed to generate logic. Please try again later.', 'error');
    } finally {
      setLogicLoading(false)
    }
  }
  
  const [selectedSubparameters, setSelectedSubparameters] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [allowedSubparameters, setAllowedSubparameters] = useState([]);
  
  // Filtered subparameters based on input
  const filteredSubparameters = allowedSubparameters.filter(
    subparam => subparam.toLowerCase().includes(selectedSubparameter.toLowerCase()) && 
                !selectedSubparameters.includes(subparam)
  );
  
  const handleSelectSubparameter = (subparam) => {
    setSelectedSubparameter(subparam);
    setShowDropdown(false);
  };
  const toVariableName = (label) => {
    return label.trim().toLowerCase().replace(/\W+/g, '_');
  };

  /** Parameters and logic-based subparameters that depend on this subparameter (archive / deactivate blockers). */
  const getSubparameterArchiveBlockers = (subparamOrId) => {
    const subparam =
      typeof subparamOrId === 'object' && subparamOrId !== null
        ? subparamOrId
        : subparameters.find((s) => s.id === subparamOrId);
    if (!subparam) return { usedByParameters: [], usedByLogicSubparams: [] };
    const varName = toVariableName(subparam.display_name);
    const usedByParameters = parameters.filter(
      (p) => p.subparameters_used && p.subparameters_used.includes(varName)
    );
    const usedByLogicSubparams = subparameters.filter(
      (s) =>
        s.id !== subparam.id &&
        s.subparameter_type === 'logic_based' &&
        s.subparameters_used &&
        s.subparameters_used.includes(varName)
    );
    return { usedByParameters, usedByLogicSubparams };
  };

  const isSubparameterUsed = (subparamId) => {
    const { usedByParameters, usedByLogicSubparams } = getSubparameterArchiveBlockers(subparamId);
    return usedByParameters.length > 0 || usedByLogicSubparams.length > 0;
  };

  const serializeTrainingMaterials = () => {
    try {
      const obj = {};
      (trainingMaterials || []).forEach((item) => {
        const key = item?.parameter_key || (item?.parameter_display_name ? toVariableName(item.parameter_display_name) : null);
        if (!key) return;
        const mats = Array.isArray(item?.materials) ? item.materials : [];
        const cleaned = mats
          .map(m => ({ name: (m?.name || m?.material_name || '').trim(), link: (m?.link || m?.material_link || '').trim() }))
          .filter(m => m.name !== '' || m.link !== '');
        obj[key] = {
          display_name: item?.parameter_display_name || key,
          materials: cleaned
        };
      });
      return obj;
    } catch (e) {
      return {};
    }
  };
  
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && selectedSubparameter) {
      e.preventDefault();
      handleAddSubparameterToQuery();
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (subparamVarDropdownRef.current && !subparamVarDropdownRef.current.contains(event.target) &&
          subparamVarInputRef.current && !subparamVarInputRef.current.contains(event.target)) {
        setSubparamShowVarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [editingParameter, setEditingParameter] = useState(null);

  // Logic-based subparameter states
  const [subparameterKind, setSubparameterKind] = useState('llm_based');
  const [classifierCriteria, setClassifierCriteria] = useState({});
  const [classifierScoreMax, setClassifierScoreMax] = useState(undefined);
  const [subparamLogic, setSubparamLogic] = useState('');
  const [subparamLogicExplanation, setSubparamLogicExplanation] = useState('');
  const [subparamLogicError, setSubparamLogicError] = useState('');
  const [subparamLogicLoading, setSubparamLogicLoading] = useState(false);
  const [subparamSelectedVars, setSubparamSelectedVars] = useState([]);
  const [subparamSelectedVar, setSubparamSelectedVar] = useState('');
  const [subparamShowVarDropdown, setSubparamShowVarDropdown] = useState(false);
  const subparamVarInputRef = useRef(null);
  const subparamVarDropdownRef = useRef(null);

  // Helper: get sorted subparameters for display (system -> client -> llm_based -> logic_based)
  const getSortedSubparameters = (subparams) => {
    const systemParams = subparams.filter(s => s.system === 1);
    const clientParams = subparams.filter(s => s.system === 2);
    const llmParams = subparams.filter(s => !s.system && s.subparameter_type !== 'logic_based');
    const logicParams = subparams.filter(s => !s.system && s.subparameter_type === 'logic_based');
    // Sort logic params by execution_order
    logicParams.sort((a, b) => (a.execution_order || 0) - (b.execution_order || 0));
    return [...systemParams, ...clientParams, ...llmParams, ...logicParams];
  };

  // Helper: get allowed variables for a logic_based subparameter at a given position
  const getLogicSubparamAllowedVars = (currentSubparamId) => {
    const sorted = getSortedSubparameters(subparameters);
    const systemParams = sorted.filter(s => s.system === 1 && s.status !== 'archived');
    const clientParams = sorted.filter(s => s.system === 2 && s.status !== 'archived');
    const llmParams = sorted.filter(s => !s.system && s.subparameter_type !== 'logic_based' && s.status !== 'archived');
    const logicParams = sorted.filter(s => !s.system && s.subparameter_type === 'logic_based' && s.status !== 'archived');

    const vars = [];
    // Add system, client, llm params
    [...systemParams, ...clientParams, ...llmParams].forEach(s => {
      vars.push(toVariableName(s.display_name));
    });
    // Add logic params that are above the current one (lower execution_order)
    const currentParam = subparameters.find(s => s.id === currentSubparamId);
    const currentOrder = currentParam?.execution_order || Infinity;
    logicParams.forEach(s => {
      if (s.id !== currentSubparamId && (s.execution_order || 0) < currentOrder) {
        vars.push(toVariableName(s.display_name));
      }
    });
    // Add prev_ prefixed versions of ALL subparameters
    subparameters.filter(s => s.status !== 'archived').forEach(s => {
      vars.push('prev_' + toVariableName(s.display_name));
    });
    return vars;
  };

  // Filtered vars for logic-based subparam picker
  const filteredSubparamVars = (() => {
    const allowed = getLogicSubparamAllowedVars(editingParameter?.id);
    return allowed.filter(
      v => v.toLowerCase().includes(subparamSelectedVar.toLowerCase()) &&
           !subparamSelectedVars.includes(v)
    );
  })();

  const addVarToSubparam = () => {
    const allowed = getLogicSubparamAllowedVars(editingParameter?.id);
    if (subparamSelectedVar && !subparamSelectedVars.includes(subparamSelectedVar) && allowed.includes(subparamSelectedVar)) {
      setSubparamSelectedVars([...subparamSelectedVars, subparamSelectedVar]);
      setSubparamSelectedVar('');
      setSubparamShowVarDropdown(false);
    }
  };

  const removeVarFromSubparam = (varName) => {
    setSubparamSelectedVars(subparamSelectedVars.filter(v => v !== varName));
  };

  // Build synthetic vars list that includes prev_ prefixed entries for the parser
  const buildSyntheticVarsForLogic = (subparams, selectedVars) => {
    // Start with actual subparameters
    const syntheticVars = subparams.map(s => ({
      display_name: toVariableName(s.display_name),
      output_type: s.output_type === 'Number' ? 'Number' : 'Text',
      possible_values: s.possible_values || []
    }));
    // Add prev_ prefixed versions for any prev_ vars in selectedVars
    selectedVars.filter(v => v.startsWith('prev_')).forEach(v => {
      const originalName = v.slice(5); // remove 'prev_'
      const original = subparams.find(s => toVariableName(s.display_name) === originalName);
      syntheticVars.push({
        display_name: v,
        output_type: original?.output_type === 'Number' ? 'Number' : 'Text',
        possible_values: original?.possible_values || []
      });
    });
    return syntheticVars;
  };

  // Verify logic for logic-based subparameter
  const verifySubparamLogic = async (logicToVerify = null) => {
    setSubparamLogicLoading(true);
    const logicToUse = logicToVerify || subparamLogic;
    if (!logicToUse.trim()) {
      setSubparamLogicLoading(false);
      setSubparamLogicError('Logic cannot be empty');
      return false;
    }
    try {
      const expectedReturn = subparameterType === 'Number' ? 'numeric' : 'string';
      const matchType = subparameterType === 'Number' ? 'exact' : 'subset';
      const syntheticVars = buildSyntheticVarsForLogic(subparameters, subparamSelectedVars);
      const result = await server.verifyQuery(client_id, service_id, logicToUse, subparamSelectedVars, syntheticVars, expectedReturn, matchType);
      if (result.status === 200) {
        let resp = await result.json();
        setSubparamLogicExplanation(resp.logic_explanation || '');
        if (resp.errors && resp.errors.length > 0) {
          setSubparamLogicLoading(false);
          setSubparamLogicError(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description']);
          return false;
        } else {
          setSubparamLogicLoading(false);
          setSubparamLogicError('');
          setSubparamLogic(resp.formatted || logicToUse);
          return true;
        }
      } else {
        setSubparamLogicLoading(false);
        setSubparamLogicError(result.errorResponse?.error || 'Error verifying logic');
        return false;
      }
    } catch (error) {
      setSubparamLogicLoading(false);
      setSubparamLogicError('Error verifying logic');
      return false;
    }
  };

  // Get next execution_order for a new logic_based subparam
  const getNextExecutionOrder = () => {
    const logicParams = subparameters.filter(s => s.subparameter_type === 'logic_based' && s.status !== 'archived');
    if (logicParams.length === 0) return 1;
    return Math.max(...logicParams.map(s => s.execution_order || 0)) + 1;
  };

  // Check if moving a logic subparam up/down is allowed (dependency check)
  const canMoveLogicSubparam = (subparamId, direction) => {
    const logicParams = subparameters
      .filter(s => s.subparameter_type === 'logic_based' && s.status !== 'archived')
      .sort((a, b) => (a.execution_order || 0) - (b.execution_order || 0));

    const idx = logicParams.findIndex(s => s.id === subparamId);
    if (idx === -1) return false;
    if (direction === 'up' && idx === 0) return false;
    if (direction === 'down' && idx === logicParams.length - 1) return false;

    const current = logicParams[idx];
    const currentVarName = toVariableName(current.display_name);

    if (direction === 'up') {
      const above = logicParams[idx - 1];
      const aboveVarName = toVariableName(above.display_name);
      // Can't move up if current depends on the one above
      if (current.subparameters_used && current.subparameters_used.includes(aboveVarName)) {
        return false;
      }
    } else {
      const below = logicParams[idx + 1];
      // Can't move down if the one below depends on current
      if (below.subparameters_used && below.subparameters_used.includes(currentVarName)) {
        return false;
      }
    }
    return true;
  };

  // Serialize subparameters to config format (handles both llm_based and logic_based)
  const serializeSubparameters = (subparams) => {
    return subparams.reduce((acc, subparam) => {
      let backendType = subparam.output_type || 'Yes/No';
      if (subparam.output_type === 'Categorical - Single Value') {
        backendType = 'Categorical';
      }
      const key = subparam.display_name.toLowerCase().replace(/\s+/g, '_');
      const base = {
        output_type: backendType,
        system: subparam.system || 0,
        display_name: subparam.display_name,
        possible_values: subparam.possible_values,
        status: subparam.status || 'active',
        ...classifierSettings(subparam),
        subparameter_type: subparam.subparameter_type || 'llm_based',
      };
      if (subparam.subparameter_type === 'logic_based') {
        base.logic = subparam.logic || '';
        base.subparameters_used = subparam.subparameters_used || [];
        base.execution_order = subparam.execution_order || 0;
      } else {
        base.instruction = subparam.instruction || '';
        base.context_scope = subparam.context_scope || 'conversation';
        Object.assign(base, callTypeSettings(subparam));
      }
      acc[key] = base;
      return acc;
    }, {});
  };

  // Move logic subparam up or down
  const moveLogicSubparam = (subparamId, direction) => {
    if (!canMoveLogicSubparam(subparamId, direction)) {
      showToast('Cannot reorder: dependency conflict. Remove the dependency first.', 'error');
      return;
    }
    const logicParams = subparameters
      .filter(s => s.subparameter_type === 'logic_based' && s.status !== 'archived')
      .sort((a, b) => (a.execution_order || 0) - (b.execution_order || 0));

    const idx = logicParams.findIndex(s => s.id === subparamId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;

    // Swap execution_orders
    const currentOrder = logicParams[idx].execution_order;
    const swapOrder = logicParams[swapIdx].execution_order;

    setSubparameters(prev => prev.map(s => {
      if (s.id === logicParams[idx].id) return { ...s, execution_order: swapOrder };
      if (s.id === logicParams[swapIdx].id) return { ...s, execution_order: currentOrder };
      return s;
    }));
  };
  

  // Modal states
  const [showSubparameterModal, setShowSubparameterModal] = useState(false);
  const [parameter_name, setParameter_name] = useState('');
  
  const [manualQuery, setManualQuery] = useState('');
  const [queryError, setQueryError] = useState('');
  let [isEdit, setIsEdit] = useState(false);
  const [showParameterForm, setShowParameterForm] = useState(false);
  const parameterFormRef = useRef(null);

  // Add these state variables at the top with other states
  const [subparameterName, setSubparameterName] = useState('');
  const [subparameterDefinition, setSubparameterDefinition] = useState('');
  const [contextScope, setContextScope] = useState('conversation');
  const [subparameterType, setSubparameterType] = useState('Yes/No/NA');
  const [subparameterTags, setSubparameterTags] = useState([]);
  const [subparameterPossibleValues, setSubparameterPossibleValues] = useState([])
  const [currentTag, setCurrentTag] = useState('');

  // Add this new state near other state declarations
  const [showLogicInfo, setShowLogicInfo] = useState(false);

  const handleOpenParameterForm = () => {
    setError(null)
    setIsValid(false)
    setShowParameterForm(true);
    setLogicExplanation('')
    setIsEdit(false);
    setEditingParameterId(null); // Reset editing parameter ID
    setParameter_name('');
    setSelectedSubparameters([]);
    setSelectedSubparameter('');
    setManualQuery('');
    setQueryError('');
    setPromptQuery('')
    setWriteWithAiEnabled(false) // Reset AI state
    let arr = []
    for(let i=0;i<subparameters.length;i++){
      arr.push(toVariableName(subparameters[i].display_name));
    }
    setAllowedSubparameters(arr);
    
    // Smooth scroll after state updates
    setTimeout(() => {
      parameterFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleEditSubparameter = async (id) => {
    setError(null)
    const subparam = subparameters.find(p => p.id === id);
    if (subparam) {
      const kind = subparam.subparameter_type || 'llm_based';
      setSubparameterKind(kind);
      setClassifierCriteria(subparam.classifier_criteria || {});
      setClassifierScoreMax(subparam.classifier_score_max);

      if (kind !== 'logic_based') {
        if(kind !== 'classifier' && (subparam.type === 'Categorical - Single Value' || subparam.type === 'Categorical - Multiple Values' || subparam.type === 'Yes/No/NA' || subparam.type === 'Number')){
          let flagResponse = await checkDefinitionValidity(subparam.instruction, subparam.type, subparam.possible_values)
          if(flagResponse['error']){
            setDescriptionError(flagResponse['error'])
            showToast(flagResponse['error'], "error")
            return;
          }else{
            setDescriptionError(null)
            showToast("Definition is valid", "success")
          }
        }
        setSubparameterDefinition(subparam.instruction || '');
        setContextScope(subparam.context_scope || 'conversation');
        setParameterExecution(callTypeSettings(subparam));
        setSubparamLogic('');
        setSubparamLogicExplanation('');
        setSubparamLogicError('');
        setSubparamSelectedVars([]);
        setSubparamSelectedVar('');
      } else {
        // logic_based
        setSubparamLogic(subparam.logic || '');
        setSubparamLogicExplanation('');
        setSubparamLogicError('');
        setSubparamSelectedVars(subparam.subparameters_used || []);
        setSubparamSelectedVar('');
        setSubparameterDefinition('');
        setContextScope('conversation');
        setParameterExecution({});
      }

      setSubparameterName(subparam.display_name);
      // Convert backend types to frontend display types
      let displayType = subparam.output_type || 'Yes/No/NA';
      if (subparam.output_type === 'Categorical') {
        displayType = 'Categorical - Single Value';
      }
      setSubparameterType(displayType);
      setSubparameterTags(subparam.options || []);
      setSubparameterPossibleValues(subparam.possible_values || [])
      setEditingParameter(subparam);
      setShowSubparameterModal(true);
      setTimeout(() => {
        const element = document.getElementById('editing-parameter');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };
  // Training Materials helpers
  const openAddTrainingMaterial = () => {
    setEditingTrainingIndex(null);
    setSelectedTrainingParamDisplay('');
    setSelectedTrainingParamKey('');
    setMaterialRows([{ name: '', link: '' }]);
    setShowTrainingMaterialForm(true);
    setTimeout(() => {
      trainingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const openEditTrainingMaterial = (idx) => {
    const tm = trainingMaterials[idx];
    if (!tm) return;
    setEditingTrainingIndex(idx);
    setSelectedTrainingParamDisplay(tm.parameter_display_name || '');
    setSelectedTrainingParamKey(tm.parameter_key || '');
    setMaterialRows(Array.isArray(tm.materials) && tm.materials.length
      ? tm.materials.map(m => ({ name: m.name || m.material_name || '', link: m.link || m.material_link || '' }))
      : [{ name: '', link: '' }]
    );
    setShowTrainingMaterialForm(true);
    setTimeout(() => {
      trainingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleRemoveTrainingMaterial = (idx) => {
    setTrainingMaterials(prev => {
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const addMaterialRow = () => {
    setMaterialRows(prev => [...prev, { name: '', link: '' }]);
  };

  const removeMaterialRow = (idx) => {
    setMaterialRows(prev => {
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const handleMaterialRowChange = (idx, field, value) => {
    setMaterialRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const saveTrainingMaterial = () => {
    try {
      if (!selectedTrainingParamDisplay) {
        showToast('Please select a parameter', 'error');
        return;
      }
      const param = parameters.find(p => p.display_name === selectedTrainingParamDisplay);
      const paramKey = param ? toVariableName(param.display_name) : selectedTrainingParamKey;
      if (!paramKey) {
        showToast('Invalid parameter selected', 'error');
        return;
      }
      const cleaned = materialRows
        .map(m => ({ name: (m.name || '').trim(), link: (m.link || '').trim() }))
        .filter(m => m.name !== '' || m.link !== '');
      if (cleaned.length === 0) {
        showToast('Please add at least one material with name or link', 'error');
        return;
      }
      const item = {
        parameter_key: paramKey,
        parameter_display_name: param?.display_name || selectedTrainingParamDisplay || paramKey,
        materials: cleaned
      };
      setTrainingMaterials(prev => {
        const copy = [...prev];
        const existingIdx = editingTrainingIndex !== null ? editingTrainingIndex : copy.findIndex(t => t.parameter_key === paramKey);
        if (existingIdx !== -1) {
          copy[existingIdx] = item;
          return copy;
        }
        return [...copy, item];
      });
      setShowTrainingMaterialForm(false);
      setEditingTrainingIndex(null);
      setSelectedTrainingParamDisplay('');
      setSelectedTrainingParamKey('');
      setMaterialRows([]);
      showToast('Training material saved', 'success');
    } catch (e) {
      showToast('Failed to save training material', 'error');
    }
  };

  // Event handlers for parameters
  const [editingParameterId, setEditingParameterId] = useState(null);

  const handleEditParameter = (id) => {
    setIsValid(false)
    const param = parameters.find(p => p.id === id);
    if (param) {
      setParameter_name(param.display_name);
      setIsEdit(true);
      setEditingParameterId(id); // Track which parameter we're editing
      setSelectedSubparameters(param.subparameters_used);
      setManualQuery(param.logic);
      setLogicExplanation(param.logic_explanation)
      setShowParameterForm(true);
      let arr = []
    for(let i=0;i<subparameters.length;i++){
      arr.push(toVariableName(subparameters[i].display_name));
    }
    setAllowedSubparameters(arr);
      
      // Smooth scroll after state updates
      setTimeout(() => {
        parameterFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

// Handle changes to the query
const handleQueryChange = (query) => {
  setManualQuery(query);
};

const verifyQuery = async (queryToVerify = null) => {
  setLogicLoading(true)
  const queryToUse = queryToVerify || manualQuery;
  if (!queryToUse.trim()) {
    setLogicLoading(false)
    setQueryError('Query cannot be empty');
    return false;
  }

  try {
    const result = await server.verifyQuery(client_id, service_id, queryToUse, selectedSubparameters, subparameters, 'numeric', 'exact');
    if (result.status === 200) {
      let resp = await result.json()
      setLogicExplanation(resp.logic_explanation)
      if(resp.errors && resp.errors.length > 0){
        setLogicLoading(false)
        setQueryError(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description'])
        return false;
      }else{        
        setLogicLoading(false)
        setQueryError('');
        setIsValid(true);
        setManualQuery(resp.formatted)
        return true;
      }
    } else {
      setLogicLoading(false)
      setQueryError(result.errorResponse.error);
      return false;
    }
  } catch (error) {
    setLogicLoading(false)
    setQueryError('Error verifying query');
    return false;
  } finally {
    setLogicLoading(false)
  }
};

const [error, setError] = useState("")
  const handleChangeParams = (e, type) => {
    const value = e.target.value;
    const alphanumericWithSpaceRegex = /^[a-zA-Z0-9 ]*$/;
    
    if (!alphanumericWithSpaceRegex.test(value)) {
      setError("Only alphanumeric characters and spaces are allowed");
      return;
    }
    
    if (value.length <= 50) {
      switch (type) {
        case 'parameter':
          setParameter_name(value)
          break;
        case 'subparameter':
          setSubparameterName(value)
        default:
          break;
      }
      setError(""); // Clear error when within limit
    } else {
      setError("Maximum 50 characters allowed");
    }
};

const handleSave = async () => {
    try {
      if (!parameter_name || !parameter_name.trim()) return;
      if (hasBoundarySpace(parameter_name)) {
        setError(getBoundarySpaceNameError('Parameter name', parameter_name));
        showToast(getBoundarySpaceNameError('Parameter name', parameter_name), 'error');
        return;
      }
      let resp = await verifyQuery()
      if(resp === false){
        setLogicLoading(false); // Reset loading state on validation failure
        return;
      }
      if(queryError){
        setLogicLoading(false); // Reset loading state on query error
        showToast(queryError, "error");
        return;
      }
      let flag = false
      for(let i=0; i<parameters.length; i++){
        if(parameters[i]['display_name'].toLowerCase() === parameter_name.trim().toLowerCase() && !isEdit){
          flag = true
          break;
        }
      }
      if(flag === true){
        setLogicLoading(false); // Reset loading state on duplicate name error
        setError('Parameter name already exists.')
          return;
      }
      setParameter_name(parameter_name.trim())

      // Update parameters list
      if (isEdit) {
        // Update existing parameter using the tracked ID
        setParameters(parameters.map(param => 
          param.id === editingParameterId ? {
            ...param,
            display_name: parameter_name,
            logic: manualQuery,
            weightage: parseWeightage(param.weightage),
            fatal: param.fatal || 0,
            subparameters_used: selectedSubparameters,
            output_type: 'Number',
            logic_explanation: logicExplanation
          } : param
        ));
      } else {
        // Add new parameter
        const newParameter = {
          id: parameters.length > 0 ? parameters.length + 1 : 1,
          display_name: parameter_name,
          logic: manualQuery,
          weightage: 0,
          fatal: 0,
          subparameters_used: selectedSubparameters,
          output_type: 'Number',
          status: 'active', // Ensure status is set for new parameters
          logic_explanation: logicExplanation
        };
        setParameters([...parameters, newParameter]);
      }

      // Reset form state
      setParameter_name('');
      setIsEdit(false);
      setEditingParameterId(null); // Reset editing parameter ID
      setShowParameterForm(false);
      setWriteWithAiEnabled(false); // Reset AI state
      setPromptQuery(''); // Reset AI prompt
      setLogicLoading(false); // Reset loading state on successful save
      
      showToast('Parameter saved successfully!', "success");
    } catch (error) {
      console.error("Error saving parameter:", error);
      setLogicLoading(false); // Reset loading state on error
      showToast("Failed to save parameter!", "error");
    }
};
const [email, setEmail] = useState(null)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [moduleName, setModuleName] = useState(null)
  const [moduleActive, setModuleActive] = useState(false)

  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const handleAddSubparameter = () => {
    setError(null)
    setSubparameterKind('llm_based');
    setClassifierCriteria({});
    setClassifierScoreMax(undefined);
    setSubparamLogic('');
    setSubparamLogicExplanation('');
    setSubparamLogicError('');
    setSubparamSelectedVars([]);
    setSubparamSelectedVar('');
    setShowSubparameterModal(true);
    // Smooth scroll to the editing element
    setTimeout(() => {
      const element = document.getElementById('editing-parameter');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };
  const updateParameterFieldById = (parameterId, key, value) => {
    setParameters((prev) =>
      prev.map((item) => (item.id === parameterId ? { ...item, [key]: value } : item))
    );
  };
  const [samples, setSamples] = useState([]);
  const [defaultConfig, setDefaultConfig] = useState(null)

  const generateSamples = async (sample_flag) => {
    try {
      setLoading(true);
      
      // Transform the current state into the required configuration structure
      const config = {
        configuration: {
            criteria: criteria,
            parameters: parameters.reduce((acc, param) => {
              acc[param.display_name.toLowerCase().replace(/\s+/g, '_')] = {
                fatal: param.fatal || 0,
                logic: param.logic || '',
                weightage: parseWeightage(param.weightage),
                display_name: param.display_name,
                subparameters_used: param.subparameters_used || [],
                threshold_score: parseInt(param.threshold_score) || 0,
                output_type: "Number",
                logic_explanation: param.logic_explanation || '',
                status: param.status || 'active'
              };
              return acc;
            }, {}),
            subparameters: serializeSubparameters(subparameters),
            training_materials: serializeTrainingMaterials(),
            fatal_zero_marking: fatal_zero_marking===true ? 1 : 0,
        },
        ...moduleExecution, active: moduleActive===true ? 1 : 0
      };

      const response = await server.generateSampleModuleOutputs({
        client_id: client_id,
        service_id: Array.isArray(service_id) ? service_id[0] : service_id,
        module_name: 'agent_feedback',
        configuration: config,
        menu: "apps",
        submenu: undefined,
        email: email,
        'sample_set_flag': sample_flag
      });

      const resp = await response.json();
      if (response.status === 200) {
        // Transform the data into the format we need
        const transformedSamples = resp.data.map(sample => {
          return {
            call_id: sample.call_id,
            employee_name: sample.employee_name,
            call_time: sample.call_time,
            name: sample.name,
            parameters: sample.processed_analytics,
            record_link: sample.record_link,
            segments: Array.isArray(sample.segments) ? sample.segments : [],
            record_type: sample.record_type
          };
        });
        setSamples(transformedSamples);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error generating samples:", error);
      setLoading(false);
    }
  };
  const getDefaultModuleConfigs = async () => {
    try {
      setLoading(true);
      let res = await server.getDefaultModuleConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if (resp.data){
          setDefaultConfig(resp.data)
        }
      }
    setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }
  const getProjectStatus = async () => {
    try {
      let res = await server.getProjectStatus(client_id, service_id, email, 'apps', undefined, window.location.href)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data && resp.data === 'completed'){
          setShowSampleTest(true);
        }
      }
    } catch (error) {
    }
  }

  const getModuleConfig = async() => {
    try {
      let res = await server.getClientModuleConfig(client_id, service_id,email,'apps',undefined, moduleName);
      if(res.status === 200){
        let resp = await res.json()
        let configData = resp.data.configuration;
        setModuleDraftSaveConfiguration(resp.data)
        setCurrent_version(resp.data.version)
        function transformParameters(params) {
            return Object.keys(params).map((key, index) => {
                const param = params[key];
                // Convert backend types to frontend display types
                let displayType = param.output_type;
                if (param.output_type === 'Categorical') {
                  displayType = 'Categorical - Single Value';  // Default assumption
                }
                
                return {
                    id: index + 1,
                    status: param.status || 'active', // Ensure status is set
                    ...param,
                    output_type: displayType
                };
            });
        }
        let selectedParameters = configData?.parameters 
            ? transformParameters(configData.parameters) 
            : [];
        let selectedsubParameters = configData?.subparameters 
            ? transformParameters(configData.subparameters) 
            : [];
        let selectedcriteria = configData?.criteria ? configData.criteria : {}
        // Load training materials if present
        if (configData?.training_materials) {
          try {
            const tm = configData.training_materials;
            // expected object: { [parameter_key]: { display_name, materials: [{name, link}] } }
            const list = Object.keys(tm).map((k) => {
              const entry = tm[k] || {};
              const mats = Array.isArray(entry.materials) ? entry.materials : [];
              return {
                parameter_key: k,
                parameter_display_name: entry.display_name || k,
                materials: mats.map(m => ({ name: m.name || m.material_name || '', link: m.link || m.material_link || '' }))
              };
            });
            setTrainingMaterials(list);
          } catch (_) {}
        } else {
          setTrainingMaterials([]);
        }
        setParameters(selectedParameters)
        setSubparameters(selectedsubParameters)
        setCriteria(selectedcriteria)
        setAgentFeedbackModule(configData);
        setModuleActive(resp.data.active === 1 ? true : false)
        setModuleExecution(moduleCallTypeSettings(resp.data));
        setLoadedModuleExecution(moduleCallTypeSettings(resp.data));
        setFatal_zero_marking(configData?.fatal_zero_marking===1 ? true : false)
        // setDefaultConfig(resp.data.default.configuration)
      }
    } catch (error) {
    }
  }
  const deployModule = async(versionDescription) => {
    try {
      setLoading(true)
      let deployed = false;
      let errorMessage = 'Failed to deploy module';
      const moduleConfig = { 
        criteria: {'minimum_call_duration': parseInt(criteria.minimum_call_duration), 'audio_quality_threshold': 'Both parties interacted atleast once.', 
          'count_total_conversational_messages': parseInt(criteria.count_total_conversational_messages || 0), 'count_user_conversational_messages': parseInt(criteria.count_user_conversational_messages || 0)},
        parameters: parameters.reduce((acc, param) => {
          acc[param.display_name.toLowerCase().replace(/\s+/g, '_')] = {
            fatal: param.fatal || 0,
            logic: param.logic || '',
            weightage: parseWeightage(param.weightage),
            display_name: param.display_name,
            subparameters_used: param.subparameters_used || [],
            output_type: 'Number',
            threshold_score: parseInt(param.threshold_score) || 0,
            logic_explanation: param.logic_explanation || '',
            status: param.status || 'active' // Include status in deployment
          };
          return acc;
        }, {}),
        subparameters: serializeSubparameters(subparameters),
        training_materials: serializeTrainingMaterials(),
        fatal_zero_marking: fatal_zero_marking===true ? 1 : 0,
      }
      let config = {'configuration': moduleConfig, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
      let resp = await server.deployModule({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'agent_feedback', configuration: config, default: defaultActive, 'version_changed': current_version, version_description: versionDescription})
      if (resp.status === 200) {
        showToast('Module deployed successfully', "success")
        deployed = true;
        setLoading(false)
      }else{
        try {
          const errData = await resp.clone().json();
          errorMessage = errData?.message || errData?.error || errorMessage;
        } catch (_) {}
      }
      if (deployed) {
        setCloseModal()
      } else {
        showToast(errorMessage, 'error');
        setLoading(false)
      }
    } catch (error) {
      showToast('Failed to deploy module', 'error');
      setLoading(false)
    }
  }
  const resetToDefault = () => {
    setDefaultActive(true)
    let configData = defaultConfig.configuration;
    let default_preset = (defaultConfig.have_preset === 1) ? defaultConfig.default_preset : 'sales'
    function transformParameters(params) {
        return Object.keys(params).map((key, index) => {
            const param = params[key];
            // Convert backend types to frontend display types
            let displayType = param.output_type;
            if (param.output_type === 'Categorical') {
              displayType = 'Categorical - Single Value';  // Default assumption
            }
            
            return {
                id: index + 1,
                status: param.status || 'active', // Ensure status is set
                ...param,
                output_type: displayType
            };
        });
    }
    let selectedParameters = configData[default_preset].parameters ? transformParameters(configData[default_preset].parameters) : [];
    let selectedsubParameters = configData[default_preset].subparameters ? transformParameters(configData[default_preset].subparameters) : [];
    let selectedcriteria = configData[default_preset].criteria ? configData[default_preset].criteria : {}
    setFatal_zero_marking(configData[default_preset]?.fatal_zero_marking===1 ? true : false)
    setParameters(selectedParameters)
    setSubparameters(selectedsubParameters)
    setCriteria(selectedcriteria)
    setAgentFeedbackModule(configData[default_preset]);
    setModuleActive(true);
    setModuleExecution(moduleCallTypeSettings(defaultConfig || {}));
    setLoadedModuleExecution(moduleCallTypeSettings(defaultConfig || {}));
    setActiveTab('individual')
    // toast.success('Configuration reset to default')
  }
  const handleSaveSubparameter = async () => {
    setShowLoadConfigurePart(true)
    if (!subparameterName.trim()) {
      showToast('Subparameter name is required', "error");
      setShowLoadConfigurePart(false)
      return;
    }
    if (hasBoundarySpace(subparameterName)) {
      const errorMessage = getBoundarySpaceNameError('Subparameter name', subparameterName);
      setError(errorMessage);
      showToast(errorMessage, "error");
      setShowLoadConfigurePart(false);
      return;
    }
    let subparamName = subparameterName.trim()
    let flag = false;
    for(let i=0;i<subparameters.length;i++){
      if(subparameters[i]['display_name'].toLowerCase() === subparamName.toLowerCase() && !editingParameter?.id){
        flag = true;
        break;
      }
    }
    if(flag === true){
      setError('Subparameter name already exists.')
      setShowLoadConfigurePart(false)
      return;
    }

    // Convert frontend display types to backend expected types
    let backendType = subparameterType;
    if (subparameterType === 'Categorical - Single Value') {
      backendType = 'Categorical';
    }

    const classificationError = classifierError({ subparameter_type: subparameterKind, classifier_criteria: classifierCriteria, classifier_score_max: classifierScoreMax, output_type: backendType, possible_values: subparameterPossibleValues, instruction: subparameterDefinition });
    if (classificationError) {
      showToast(classificationError, 'error');
      setShowLoadConfigurePart(false);
      return;
    }
    let newSubparameter;

    if (subparameterKind === 'logic_based') {
      // Validate logic
      if (!subparamLogic.trim()) {
        showToast('Logic is required for logic-based subparameters', 'error');
        setShowLoadConfigurePart(false);
        return;
      }
      if (subparamSelectedVars.length === 0) {
        showToast('Please select at least one variable for the logic', 'error');
        setShowLoadConfigurePart(false);
        return;
      }
      // Run logic validation via verifyQuery
      try {
        const expectedReturn = subparameterType === 'Number' ? 'numeric' : 'string';
        const matchType = subparameterType === 'Number' ? 'exact' : 'subset';
        const syntheticVars = buildSyntheticVarsForLogic(subparameters, subparamSelectedVars);
        const result = await server.verifyQuery(client_id, service_id, subparamLogic, subparamSelectedVars, syntheticVars, expectedReturn, matchType);
        if (result.status === 200) {
          let resp = await result.json();
          if (resp.errors && resp.errors.length > 0) {
            setSubparamLogicError(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description']);
            showToast(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description'], 'error');
            setShowLoadConfigurePart(false);
            return;
          } else {
            setSubparamLogicError('');
            if (resp.formatted) setSubparamLogic(resp.formatted);
            if (resp.logic_explanation) setSubparamLogicExplanation(resp.logic_explanation);
          }
        } else {
          showToast('Error validating logic', 'error');
          setShowLoadConfigurePart(false);
          return;
        }
      } catch (error) {
        showToast('Error validating logic', 'error');
        setShowLoadConfigurePart(false);
        return;
      }

      newSubparameter = {
        id: editingParameter?.id ? editingParameter.id : subparameters.length+1,
        display_name: subparamName,
        subparameter_type: 'logic_based',
        logic: subparamLogic,
        subparameters_used: subparamSelectedVars,
        execution_order: editingParameter?.execution_order || getNextExecutionOrder(),
        output_type: backendType,
        possible_values: subparameterType === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : subparameterPossibleValues,
        options: subparameterTags,
        system: 0,
        status: editingParameter?.status || 'active',
      };
    } else {
      // llm_based validation
      if(subparameterKind !== 'classifier' && (subparameterType === 'Categorical - Single Value' || subparameterType === 'Categorical - Multiple Values' || subparameterType === 'Yes/No/NA' || subparameterType === 'Number' || subparameterType === 'Date and Time')){
        let flagResponse = await checkDefinitionValidity(subparameterDefinition, subparameterType, subparameterPossibleValues)
        if(flagResponse['error']){
          setDescriptionError(flagResponse['error'])
          showToast(flagResponse['error'], "error")
          setShowLoadConfigurePart(false)
          return;
        }else{
          setDescriptionError(null)
        }
      }

      newSubparameter = {
        id: editingParameter?.id ? editingParameter.id : subparameters.length+1,
        display_name: subparamName,
        subparameter_type: subparameterKind,
        ...classifierSettings({ subparameter_type: subparameterKind, classifier_criteria: classifierCriteria, classifier_score_max: classifierScoreMax, output_type: backendType, possible_values: subparameterPossibleValues }),
        instruction: subparameterDefinition,
        context_scope: contextScope,
        ...parameterExecution,
        output_type: backendType,
        possible_values: subparameterType === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : subparameterPossibleValues,
        options: subparameterTags,
        status: editingParameter?.status || 'active',
      };
    }

    if (editingParameter?.id) {
      // Update existing subparameter
      setSubparameters(prevSubparameters =>
        prevSubparameters.map(p =>
          p.id === editingParameter.id ? (() => {
            const updated = { ...p, ...newSubparameter };
            if (subparameterKind !== 'classifier') {
              delete updated.classifier_criteria;
              delete updated.classifier_score_max;
            }
            return updated;
          })() : p
        )
      );
      showToast('Subparameter updated successfully', "success");
    } else {
      let flag = false
      for(let i=0; i<subparameters.length; i++){
        if(subparameters[i]['display_name'] === subparameterName){
          flag = true
          break;
        }
      }
      if(flag === true){
        setError('Subparameter name already exists.')
        setShowLoadConfigurePart(false)
          return;
      }
      setSubparameterName(subparameterName.trim())
      // Add new subparameter
      setSubparameters(prevSubparameters => [...prevSubparameters, newSubparameter]);
      showToast('Subparameter saved successfully', "success");
    }

    // Reset form fields
    setSubparameterName('');
    setSubparameterDefinition('');
    setContextScope('conversation');
    setParameterExecution({});
    setSubparameterType('Yes/No/NA');
    setSubparameterPossibleValues([])
    setSubparameterTags([]);
    setEditingParameter(null);
    setShowSubparameterModal(false);
    setShowLoadConfigurePart(false)
    setSubparameterKind('llm_based');
    setClassifierCriteria({});
    setClassifierScoreMax(undefined);
    setSubparamLogic('');
    setSubparamLogicExplanation('');
    setSubparamLogicError('');
    setSubparamSelectedVars([]);
    setSubparamSelectedVar('');
  };
  const handleAddTag = () => {
    if (currentTag.trim() === '') return;
    setSubparameterTags([...subparameterTags, currentTag]);
    setSubparameterPossibleValues([...subparameterPossibleValues, currentTag])
    setCurrentTag('');
  }
  const handleRemoveTag = (tag) => {
    setSubparameterTags(subparameterTags.filter(t => t !== tag));
    setSubparameterPossibleValues(subparameterPossibleValues.filter(t => t !== tag))
  }
  const getModuleDraftSaveConfiguration = async () => {
    try {
      let res = await server.getModuleDraftSaveConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data){
          let configData = resp.data.configuration;
          function transformParameters(params) {
            return Object.keys(params).map((key, index) => {
                const param = params[key];
                // Convert backend types to frontend display types
                let displayType = param.output_type;
                if (param.output_type === 'Categorical') {
                  displayType = 'Categorical - Single Value';  // Default assumption
                }
                
                return {
                    id: index + 1,
                    ...param,
                    output_type: displayType
                };
            });
        }
        let selectedParameters = configData?.parameters 
            ? transformParameters(configData.parameters) 
            : [];
        let selectedsubParameters = configData?.subparameters 
            ? transformParameters(configData.subparameters) 
            : [];
        let selectedcriteria = configData?.criteria ? configData.criteria : {}
        if (configData?.training_materials) {
          try {
            const tm = configData.training_materials;
            const list = Object.keys(tm).map((k) => {
              const entry = tm[k] || {};
              const mats = Array.isArray(entry.materials) ? entry.materials : [];
              return {
                parameter_key: k,
                parameter_display_name: entry.display_name || k,
                materials: mats.map(m => ({ name: m.name || m.material_name || '', link: m.link || m.material_link || '' }))
              };
            });
            setTrainingMaterials(list);
          } catch (_) {}
        } else {
          setTrainingMaterials([]);
        }
        setFatal_zero_marking(configData?.fatal_zero_marking===1 ? true : false)
        setParameters(selectedParameters)
        setSubparameters(selectedsubParameters)
        setCriteria(selectedcriteria)
        setAgentFeedbackModule(configData);
        setModuleActive(resp.data.active === 1 ? true : false)
        setModuleExecution(moduleCallTypeSettings(resp.data));
        setLoadedModuleExecution(moduleCallTypeSettings(resp.data));
        }else{
          showToast('No saved draft.', "error")
          setConfigType('Live Version')
        }
        // resetToDefault('draft')
      }
    } catch (error) {
    }
  }
  // Load new audio when sample changes
  useEffect(() => {
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
        if (user && clientID) {
          setEmail(user.email);
          setClient_id(clientID);
        }
        if (serviceID && module_name) {
          setService_id(serviceID);
          setModuleName(module_name)
        }
      }, [clientID, serviceID, module_name]);
    
      useEffect(() => {
        if (client_id && service_id && moduleName && email) {
          getModuleConfig();
          getDefaultModuleConfigs()
          getProjectStatus()
        }
      }, [client_id, service_id, moduleName, email]);
      useEffect(()=> {
        if(configType === 'Saved Draft'){
          getModuleDraftSaveConfiguration();
        }
        if(configType === 'Default'){
          resetToDefault();
        }
        if(configType === 'Live Version' && client_id && service_id && moduleName && email){
          getModuleConfig();
        }
      }, [configType])
      const handleUpdateConfig = async () => {
        try {
          const moduleConfig = { 
            criteria: {'minimum_call_duration': parseInt(criteria?.minimum_call_duration), 'audio_quality_threshold': 'Both parties interacted atleast once.',
              'count_total_conversational_messages': parseInt(criteria.count_total_conversational_messages || 0), 'count_user_conversational_messages': parseInt(criteria.count_user_conversational_messages || 0)
            },
            parameters: parameters?.reduce((acc, param) => {
              acc[param.display_name.toLowerCase().replace(/\s+/g, '_')] = {
                fatal: param.fatal || 0,
                logic: param.logic || '',
                weightage: parseWeightage(param.weightage),
                display_name: param.display_name,
                subparameters_used: param.subparameters_used || [],
                output_type: 'Number',
                threshold_score: parseInt(param.threshold_score) || 0
              };
              return acc;
            }, {}),
            subparameters: serializeSubparameters(subparameters || []),
            training_materials: serializeTrainingMaterials(),
            fatal_zero_marking: fatal_zero_marking===true ? 1 : 0
          }
          let config = {'configuration': moduleConfig, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
          let res = await server.postModuleDraftSaveConfiguration({client_id, service_id, email, module_name: 'agent_feedback', 'configuration': config, 'menu': 'apps', 'submenu': undefined})
          if(res.status === 200) {
            showToast(await getApiToastMessage(res, 'Draft saved successfully'), "success")
          } else {
            showToast(await getApiToastMessage(res, 'Failed to save draft'), "error")
          }
        } catch (error) {
          showToast('Failed to save draft', "error")
      }
    }

      useEffect(()=> {
        if(moduleDraftSaveConfiguration){
          const moduleConfig = { 
            criteria: {'minimum_call_duration': parseInt(criteria?.minimum_call_duration), 'audio_quality_threshold': 'Both parties interacted atleast once.',
              'count_total_conversational_messages': parseInt(criteria.count_total_conversational_messages || 0), 'count_user_conversational_messages': parseInt(criteria.count_user_conversational_messages || 0)
            },
            parameters: parameters?.reduce((acc, param) => {
              acc[param.display_name.toLowerCase().replace(/\s+/g, '_')] = {
                fatal: param.fatal || 0,
                logic: param.logic || '',
                weightage: parseWeightage(param.weightage),
                display_name: param.display_name,
                subparameters_used: param.subparameters_used || [],
                output_type: 'Number',
                threshold_score: parseInt(param.threshold_score) || 0
              };
              return acc;
            }, {}),
            subparameters: serializeSubparameters(subparameters || []),
            training_materials: serializeTrainingMaterials(),
            fatal_zero_marking: fatal_zero_marking===true ? 1 : 0,
          }
          let config = {'configuration': moduleConfig, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
          config_data(config)
        }
      }, [moduleDraftSaveConfiguration, moduleActive, moduleExecution, parameters, subparameters, criteria])
  const getCurrentConfigForDiff = () => ({
    criteria: {
      minimum_call_duration: parseInt(criteria?.minimum_call_duration) || 0,
      audio_quality_threshold: 'Both parties interacted atleast once.',
      count_total_conversational_messages: parseInt(criteria?.count_total_conversational_messages || 0),
      count_user_conversational_messages: parseInt(criteria?.count_user_conversational_messages || 0),
    },
    parameters: parameters?.reduce((acc, param) => {
      acc[param.display_name.toLowerCase().replace(/\s+/g, '_')] = {
        fatal: param.fatal || 0,
        logic: param.logic || '',
        weightage: parseWeightage(param.weightage),
        display_name: param.display_name,
        subparameters_used: param.subparameters_used || [],
        output_type: 'Number',
        threshold_score: parseInt(param.threshold_score) || 0,
        logic_explanation: param.logic_explanation || '',
        status: param.status || 'active',
      };
      return acc;
    }, {}),
    subparameters: serializeSubparameters(subparameters || []),
    training_materials: serializeTrainingMaterials(),
    fatal_zero_marking: fatal_zero_marking === true ? 1 : 0,
  });

  const totalWeightage = getTotalWeightage(parameters);
  const totalWeightageValid = isTotalWeightageValid(parameters);
  
  // Configuration Screen
  if (!showPreview) {
    return (
      <div className="p-3 h-full min-h-0 overflow-hidden">
        <div className="mx-auto flex h-full min-h-0 flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 shrink-0">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">Agent Feedback 
                <span className='inline-flex items-center px-2 py-1 rounded-full whitespace-nowrap font-normal text-xs bg-gray-200 ml-1' style={{color: "gray"}}>{configType.replace(/^Reset to /, '')}</span></h1>
              <p className="text-gray-500 text-sm mt-1">Configure and customize agent evaluation parameters</p>
              
              {/* Configuration Import/Export Buttons */}
              {role === 'superadmin' && <div className="flex items-center gap-2 mt-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadConfig}
                    className="hidden"
                    id="config-upload"
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center text-sm"
                    onClick={() => document.getElementById('config-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Config
                  </button>
                </label>
                
                <button
                  type="button"
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center text-sm"
                  onClick={handleDownloadConfig}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Config
                </button>
              </div>}
            </div>
            <div className="flex flex-wrap items-center gap-y-2">
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input class="global-input"
                    type="checkbox" 
                    checked={moduleActive} 
                    onChange={(e) => setModuleActive(moduleActive===true ? false : true)}
                    className="sr-only"
                    id="toggle-all"
                  />
                  <label 
                    htmlFor="toggle-all"
                    className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive===true ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span 
                      className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive===true ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </label>
                </div>
                <button 
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center"
                  onClick={handleUpdateConfig}
                >
                  <Save className="w-4 h-4 mr-2" /> Save as Draft
                </button>
                <ConfigurationDiffButton
                  title="Agent Feedback Changes"
                  originalConfig={() => configWithExecutionSettings(agentFeedbackModule, loadedModuleExecution)}
                  modifiedConfig={() => configWithExecutionSettings(getCurrentConfigForDiff(), moduleExecution)}
                />
                {showSampleTest && <button 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                onClick={() => setShowSampleTestModal(true)}
              >
                Manage Test Set
              </button>}
                <div className="relative inline-block mx-2">
                  <button
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                    onClick={() => setShowConfigDropdown((prev) => !prev)} style={{width: "10rem", justifyContent: "space-between"}}
                    type="button"
                  >
                    {configType}
                    <span className="ml-2">
                      {showConfigDropdown ? (
                        // Up arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        // Down arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {showConfigDropdown && (
                    <div className="absolute z-10 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {['Live Version', 'Saved Draft', 'Default'].map(option => (
                        <div
                          key={option}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${configType === option ? 'text-indigo-600' : ''}`}
                          onClick={() => {
                            setConfigType(option);
                            setShowConfigDropdown(false);
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* <ModernTimeDropdown width="w-full mx-2" placeholder="Select Configuration" value={configType} onChange={(e)=>setConfigType(e.target.value)} options={['Reset to Default', 'Reset to Saved Draft', 'New Configuration']}/> */}
                {/* <button 
                className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 mr-2" style={{height: "40px", borderRadius: "4px"}}
                onClick={resetToDefault}
              >
                Reset to Default
              </button> */}
            <button disabled={!totalWeightageValid || descriptionError}
              className={`px-4 py-2 ${!totalWeightageValid ? 'bg-gray-500 hover:bg-gray-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white rounded  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
              onClick={() => {setShowPreview(true); generateSamples(1);}}
            >
              Preview
            </button>
            </div>
          </div>
          
          {/* <div className="bg-white rounded-lg shadow p-4 mb-6">
            <h2 className="text-lg font-medium text-gray-800 mb-3">Evaluation Template</h2>
            <div className="flex gap-4">
              <button onClick={() => {handleServiceChange('sales');}} className={`px-4 py-2 ${servicetab === 'Sales' ? "bg-indigo-600 text-white hover:bg-indigo-700":""} border border-gray-300 text-gray-700 rounded hover:bg-gray-50`} style={{height: "40px", borderRadius: "4px"}}>
                Sales
              </button>
              <button onClick={() => {handleServiceChange('support');}} className={`px-4 py-2 ${servicetab === 'Support' ? "bg-indigo-600 text-white hover:bg-indigo-700":""} border border-gray-300 text-gray-700 rounded hover:bg-gray-50`} style={{height: "40px", borderRadius: "4px"}}>
                Support
              </button>
              <button onClick={() => {handleServiceChange('current');}} className={`px-4 py-2 ${servicetab === 'Current' ? "bg-indigo-600 text-white hover:bg-indigo-700":""} border border-gray-300 text-gray-700 rounded hover:bg-gray-50`} style={{height: "40px", borderRadius: "4px"}}>
                Current
              </button>
            </div>
          </div> */}
          

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 shrink-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Also run analytics for
              <span className="relative group">
                <button type="button" aria-label="About call-type settings" className="flex text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded">
                  <Info size={16} />
                </button>
                <span role="tooltip" className="absolute left-0 top-full z-50 mt-2 hidden w-64 rounded bg-gray-800 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block">
                  Proper conversations run by default when the module is active. For other conversation types, enable the module option and each required parameter separately.
                </span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {callTypeOptions.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500"
                    checked={moduleExecution[key] === 1}
                    onChange={e => setModuleExecution(prev => ({...prev, [key]: e.target.checked ? 1 : 0}))} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {!loading && <div className="bg-white rounded-lg shadow min-h-0 flex-1 overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <nav className="flex overflow-x-auto whitespace-nowrap" aria-label="Tabs">
              <button
                  onClick={() => {setActiveTab('individual');setError('')}}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'individual'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Subparameters
                </button>
                <button
                  onClick={() => {setActiveTab('parameters'); setError('');}}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'parameters'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Parameters
                </button>
                <button
                  onClick={() => setActiveTab('criteria')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'criteria'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Evaluation Criteria
                </button>
                
                <button
                  onClick={() => setActiveTab('fatal')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'fatal'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Fatal Parameters
                </button>
                <button
                  onClick={() => setActiveTab('weightage')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'weightage'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Parameter Weightage
                </button>
                <button
                  onClick={() => setActiveTab('training_material')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'training_material'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Training Material
                </button>
                
                
                
                
              </nav>
            </div>

            {activeTab === 'weightage' && (
              <div className="p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-medium text-gray-800 mb-4">Adjust Parameter Weightage</h2>
                  <p className="text-sm text-gray-600 mb-4">Total weightage should equal 100%</p>
                  
                  <div className="space-y-4">
                    {parameters.filter(param => param.status !== 'archived' && param.status !== 'inactive').map((param) => (
                      <div key={param.id} className="flex items-center space-x-4">
                        <div className="w-1/2">
                          <span className="text-gray-700">{param.display_name}</span>
                        </div>
                        <div className="w-1/2 flex items-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            onChange={(e)=> {
                              const nextValue = sanitizeWeightageInput(e.target.value);
                              if (nextValue === null) return;
                              updateParameterFieldById(param.id, 'weightage', nextValue);
                            }}
                            className="w-20 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mr-2"
                            value={param.weightage}
                          />
                          <span className="text-gray-700">%</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center space-x-4 justify-between bg-gray-100 p-2 round-lg" style={{width: "59%", borderRadius: "4px"}}>
                    <div>
                      <span className="text-gray-700">Total Weightage:</span>
                    </div>
                    <div className="flex items-center justify-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className={`w-20 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mr-2 ${totalWeightageValid ? "text-green-600" : "text-red-600"}`}
                            value={totalWeightage} disabled
                          />
                          <span className="text-gray-700">%</span>
                    </div>
                  </div>
                    {!totalWeightageValid && <div style={{color: "red", display: "flex", justifyContent: "center"}}>Total weightage should equal 100%</div>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'training_material' && (
              <div className="p-6">
                <div className="mb-4">
                  <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                    <h2 className="text-lg font-medium text-gray-800 mb-3">Training Material</h2>
                    <div className="flex flex-wrap items-center gap-4 mb-3">
                      <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search training material by parameter name" value={tableSearch.training} onChange={value => setTableSearch({ ...tableSearch, training: value })} /></div>
                      <button
                        className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 mr-2" style={{height: "40px", borderRadius: "4px"}}
                        onClick={openAddTrainingMaterial}
                      >
                        Add Material
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="configuration-table divide-y divide-gray-200">
                        <colgroup>
                          <col style={{ width: "22%" }} />
                          <col />
                          <col style={{ width: "120px" }} />
                        </colgroup>
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parameter</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {trainingMaterials.map((tm, idx) => matchesParameterName(tm, tableSearch.training) && (
                            <tr key={tm.parameter_key || idx}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tm.parameter_display_name || tm.parameter_key}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">{Array.isArray(tm.materials) && tm.materials.length > 0 ? tm.materials.map(m => m.link).filter(Boolean).join(', ') : '-'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button 
                                  className="text-indigo-600 hover:text-indigo-900 mr-4 text-md"
                                  onClick={() => openEditTrainingMaterial(idx)}
                                >
                                  <FaRegEdit size={16}/>
                                </button>
                                <button 
                                  className="text-indigo-600 hover:text-indigo-900 mr-4 text-md"
                                  onClick={() => handleRemoveTrainingMaterial(idx)}
                                >
                                  <MdDelete size={16}/>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {showTrainingMaterialForm && (
                    <div ref={trainingFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6" style={{width: "85%", margin: "auto", marginTop: "1rem"}}>
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-medium text-gray-800">{editingTrainingIndex !== null ? 'Edit Training Material' : 'Add Training Material'}</h3>
                        <button 
                          onClick={() => { setShowTrainingMaterialForm(false); setEditingTrainingIndex(null); setSelectedTrainingParamDisplay(''); setSelectedTrainingParamKey(''); setMaterialRows([]); }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <FaTimes style={{color: "slategray"}}/>
                        </button>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Parameter</label>
                        <ModernTimeDropdown 
                          width="w-full" 
                          placeholder="Select Parameter" 
                          value={selectedTrainingParamDisplay} 
                          options={parameters.filter(p => p.status !== 'archived').map(p => p.display_name)} 
                          onChange={(e) => {
                            const label = e.target.value;
                            setSelectedTrainingParamDisplay(label);
                            const found = parameters.find(p => p.display_name === label);
                            if (found) setSelectedTrainingParamKey(toVariableName(found.display_name));
                          }} 
                        />
                      </div>

                      <div className="mb-2 flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700">Materials</label>
                        <button onClick={addMaterialRow} className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Add Link</button>
                      </div>
                      <div className="space-y-2">
                        {materialRows.map((row, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input 
                              type="text" 
                              className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" 
                              placeholder="Material Name" 
                              value={row.name} 
                              onChange={(e) => handleMaterialRowChange(idx, 'name', e.target.value)}
                            />
                            <input 
                              type="text" 
                              className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" 
                              placeholder="Material Link" 
                              value={row.link} 
                              onChange={(e) => handleMaterialRowChange(idx, 'link', e.target.value)}
                            />
                            <button onClick={() => removeMaterialRow(idx)} className="px-2 py-1 text-gray-700 rounded-md hover:bg-gray-50"><MdDelete size={18} /></button>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end gap-3 mt-4">
                        <button
                          onClick={() => { setShowTrainingMaterialForm(false); setEditingTrainingIndex(null); setSelectedTrainingParamDisplay(''); setSelectedTrainingParamKey(''); setMaterialRows([]); }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveTrainingMaterial}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'fatal' && (
              <div className="p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-medium text-gray-800 mb-4">Fatal Parameters</h2>
                  <p className="text-sm text-gray-600 mb-4">Score less than threshold score on fatal parameters will result in fatal call.</p>
                  <div className='my-2'>
                    <input type="checkbox" className="mr-2" checked={fatal_zero_marking} onChange={(e) => setFatal_zero_marking(e.target.checked)}/>
                    <span className="text-sm text-gray-600">Mark overall score as 0 if any parameter is fatal</span>
                  </div>
                  
                  <div className="space-y-4">
                    {parameters.filter(param => param.status !== 'archived' && param.status !== 'inactive').map((param) => (
                      <div key={param.id} className="flex items-center space-x-4">
                        <div className="w-1/2">
                          <span className="text-gray-700">{param.display_name}</span>
                        </div>
                        <div className="w-1/2">
                          <label className="inline-flex items-center">
                          <select
                            value={param.fatal === 1 ? 'Yes' : 'No'}
                            onChange={(e) => {
                              const value = e.target.value === 'Yes' ? 1 : 0;
                              updateParameterFieldById(param.id, 'fatal', value);
                            }}
                            className="border rounded px-2 py-1"
                          >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                          {param.fatal===1 && <input type='number' className='ml-2 border rounded-md p-1 global-input w-[160px]' placeholder='Score Threshold' min="0"
                            max="100" value={param.threshold_score} onChange={(e) => {
                              const value = e.target.value
                              if (value <0 || value > 100){
                                showToast("Score threshold should be number between 0 to 100", "error")
                                return;
                              }
                              updateParameterFieldById(param.id, 'threshold_score', value);
                            }}/>}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'criteria' && (
              <div className="p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-medium text-gray-800 mb-4">Evaluation Criteria</h2>
                  <p className="text-sm text-gray-600 mb-4">Set overall criteria for evaluation</p>
                  {callTypeOptions.some(([key]) => moduleExecution[key] === 1) && (
                    <div role="note" className="mb-4 rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                      Minimum call duration and message-count criteria do not apply to{' '}
                      {callTypeOptions.filter(([key]) => moduleExecution[key] === 1).map(([, label]) => label).join(' and ')}.
                      {' '}They still apply to proper conversations.
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-1/3">
                        <span className="text-gray-700">Minimum Call Duration <span style={{fontSize: "12px", fontStyle: "italic"}}>(For calls)</span></span>
                      </div>
                      <div className="w-2/3">
                        <input
                          type="number" onChange={(e) => setCriteria(prev => ({...prev, minimum_call_duration: e.target.value}))}
                          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                          placeholder="Enter Number" value={criteria?.minimum_call_duration}
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="w-1/3">
                        <span className="text-gray-700">Minimum user conversational messages <span style={{fontSize: "12px", fontStyle: "italic"}}>(For chats)</span> </span>
                      </div>
                      <div className="w-2/3">
                        <input
                          type="number" onChange={(e) => setCriteria(prev => ({...prev, count_user_conversational_messages: e.target.value}))}
                          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                          placeholder="Enter Number" value={criteria?.count_user_conversational_messages}
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-1/3">
                        <span className="text-gray-700">Minimum total conversational messages <span style={{fontSize: "12px", fontStyle: "italic"}}>(For chats)</span></span>
                      </div>
                      <div className="w-2/3">
                        <input
                          type="number" onChange={(e) => setCriteria(prev => ({...prev, count_total_conversational_messages: e.target.value}))}
                          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                          placeholder="Enter Number" value={criteria?.count_total_conversational_messages}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="w-1/3">
                        <span className="text-gray-700">Quality Threshold</span>
                      </div>
                      <div className="w-2/3">
                        <input
                          type="text" onChange={(e) => setCriteria(prev => ({...prev, audio_quality_threshold: e.target.value}))}
                          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          defaultValue="Audio quality score GT 5"
                          placeholder="e.g. Audio quality score GT 5" value={criteria?.audio_quality_threshold} disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'individual' && (
              <div className="p-6">
                <div className="mb-4">
                 <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                 <h2 className="text-lg font-medium text-gray-800 mb-3">Subparameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search subparameters by name" value={tableSearch.subparameters} onChange={value => setTableSearch({ ...tableSearch, subparameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                      {/* Show Archived Subparameters Checkbox */}
                      <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={showArchivedSubparameters}
                          onChange={(e) => setShowArchivedSubparameters(e.target.checked)}
                          className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show Archived Subparameters
                      </label>
                      <button
                        className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                        onClick={handleAddSubparameter}
                      >
                        Add Subparameter
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="configuration-table divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "85px" }} />
                        <col style={{ width: "14%" }} />
                        <col />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "160px" }} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Definition / Logic</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kind</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parameterTableRows(getSortedSubparameters(getFilteredSubparameters()), tableSearch.subparameters).map(param => {
                          const isLogicBased = param.subparameter_type === 'logic_based';
                          const logicParams = subparameters
                            .filter(s => s.subparameter_type === 'logic_based' && s.status !== 'archived')
                            .sort((a, b) => (a.execution_order || 0) - (b.execution_order || 0));
                          const logicIdx = logicParams.findIndex(s => s.id === param.id);
                          return (
                          <tr key={param.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{param.display_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                              {isLogicBased ? (
                                <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">
                                  {(param.logic || '').length > 100 ? (param.logic || '').slice(0, 100) + '...' : param.logic}
                                </code>
                              ) : param.instruction}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {param.system === 1 ? <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">System</span> :
                               param.system === 2 ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Client</span> :
                               isLogicBased ? <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">Logic</span> :
                               param.subparameter_type === 'classifier' ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Classifier</span> :
                               <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">LLM</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.output_type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.output_type !== 'Number' ? <ConfigurationOptions options={param.possible_values} /> : '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                {/* Toggle Switch for Active/Inactive (disabled for archived or system) */}
                                {param.status !== 'archived' && !param.system && (
                                  <>
                                    <input
                                      type="checkbox"
                                      checked={!param.status || param.status === 'active'}
                                      onChange={() => handleToggleSubparameterStatus(param)}
                                      className="sr-only global-input"
                                      id={`toggle-subparam-status-${param.id}`}
                                    />
                                    <label
                                      htmlFor={`toggle-subparam-status-${param.id}`}
                                      className={`block h-5 w-12 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                                        !param.status || param.status === 'active' ? 'bg-indigo-600' : 'bg-gray-200'
                                      }`}
                                    >
                                      <span
                                        className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${
                                          !param.status || param.status === 'active' ? 'translate-x-7' : 'translate-x-0'
                                        }`}
                                      />
                                    </label>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {param.system ? (
                                <span className="text-gray-400">{param.system === 2 ? 'Client System Parameter' : 'System Parameter'}</span>
                              ) : param.status === 'archived' ? (
                                // Show restore button for archived subparameters
                                <CustomTooltip text="Restore subparameter" id={`restore-subparam-${param.id}`} position="left">
                                  <button
                                    onClick={() => handleRestoreSubparameter(param)}
                                    className="hover:text-green-600 mr-4"
                                  >
                                    <TbRestore className='h-4 w-4'/>
                                  </button>
                                </CustomTooltip>
                              ) : (
                                // Show edit, archive, and reorder buttons
                                <div className='flex items-center justify-end'>
                                  {isLogicBased && (
                                    <>
                                      <CustomTooltip text={canMoveLogicSubparam(param.id, 'up') ? "Move up" : "Cannot move up"} id={`move-up-${param.id}`} position="left">
                                        <button
                                          onClick={() => moveLogicSubparam(param.id, 'up')}
                                          disabled={logicIdx === 0}
                                          className={`mr-2 ${logicIdx === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                          <BiUpArrow className='h-3 w-3'/>
                                        </button>
                                      </CustomTooltip>
                                      <CustomTooltip text={canMoveLogicSubparam(param.id, 'down') ? "Move down" : "Cannot move down"} id={`move-down-${param.id}`} position="left">
                                        <button
                                          onClick={() => moveLogicSubparam(param.id, 'down')}
                                          disabled={logicIdx === logicParams.length - 1}
                                          className={`mr-4 ${logicIdx === logicParams.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                          <BiDownArrow className='h-3 w-3'/>
                                        </button>
                                      </CustomTooltip>
                                    </>
                                  )}
                                  <CustomTooltip text="Edit subparameter" id={`edit-subparam-${param.id}`} position="left">
                                    <button
                                      className="text-indigo-600 hover:text-indigo-900 mr-4 text-md"
                                      onClick={() => handleEditSubparameter(param.id)}
                                    >
                                      <FaRegEdit/>
                                    </button>
                                  </CustomTooltip>
                                  <CustomTooltip text="Archive subparameter" id={`archive-subparam-${param.id}`} position="left">
                                    <button
                                      onClick={() => {setShowArchiveModal(true); setArchiveItem(param); setArchiveType('subparameter');}}
                                      className="text-red-600 hover:text-red-900 mr-4"
                                    >
                                      <Archive className='h-4 w-4'/>
                                    </button>
                                  </CustomTooltip>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                 </div>
                  {showSubparameterModal && (
  <div id="editing-parameter" className="flex items-center justify-center" style={{width: "85%", margin: "auto", marginTop: "2rem"}}>
    <div className="bg-white rounded-lg w-full" style={{border: "1px solid rgb(229 231 235"}}>
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          {editingParameter?.id ? "Edit Subparameter" : "Add Subparameter"}
          {editingParameter && editingParameter.id && <div className="ml-1 relative">
            <div className="group">
              <Info className="h-4 w-4 text-gray-500 cursor-help ml-2" />
              <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                Name & Output type can't be modified.
              </div>
            </div>
          </div>}
        </h2>
        <button
          onClick={() => {setSubparameterName('');
            setSubparameterDefinition('');
            setContextScope('conversation');
            setParameterExecution({});
            setSubparameterType('Yes/No/NA');
            setSubparameterPossibleValues([]);
            setSubparameterTags([]);
            setEditingParameter(null);
            setShowSubparameterModal(false);
            setSubparameterKind('llm_based');
            setClassifierCriteria({});
            setClassifierScoreMax(undefined);
            setSubparamLogic('');
            setSubparamLogicExplanation('');
            setSubparamLogicError('');
            setSubparamSelectedVars([]);
            setSubparamSelectedVar('');}}
          className="text-gray-500 hover:text-gray-700"
        >
          <FaTimes style={{color: "slategray"}}/>
        </button>
      </div>

      {!showLoadConfigurePart && <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md global-input"
            value={subparameterName}
            onChange={(e) => handleChangeParams(e, 'subparameter')} disabled={editingParameter?.id}
          />
          <div style={{display: "flex", justifyContent: "left", color: "red", fontSize: "12px"}}>{error}</div>
        </div>

        <ClassifierFields
          value={{ subparameter_type: subparameterKind, classifier_criteria: classifierCriteria, classifier_score_max: classifierScoreMax, output_type: subparameterType, possible_values: subparameterPossibleValues, instruction: subparameterDefinition }}
          onChange={settings => { setSubparameterKind(settings.subparameter_type); setClassifierCriteria(settings.classifier_criteria || {}); setClassifierScoreMax(settings.classifier_score_max); }}
          allowLogic existingKind={editingParameter?.id ? (editingParameter.subparameter_type || 'llm_based') : undefined}
        />

        {subparameterKind !== 'logic_based' && <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Context
          </label>
          <ModernTimeDropdown
            width="w-full"
            ariaLabel="Context"
            value={contextScope === 'customer' ? 'Customer context' : 'Conversation context (default)'}
            options={['Conversation context (default)', 'Customer context']}
            onChange={(e) => setContextScope(e.target.value === 'Customer context' ? 'customer' : 'conversation')}
          />
          <p className="mt-1 text-xs text-gray-500">
            {contextScope === 'customer'
              ? 'Uses up to 50 recent conversations, including this call and non-connected calls.'
              : 'Uses this conversation only.'}
          </p>
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Run this parameter for</p>
            <div className="flex flex-wrap gap-4">
              {callTypeOptions.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500"
                    disabled={moduleExecution[key] !== 1}
                    checked={moduleExecution[key] === 1 && parameterExecution[key] === 1}
                    onChange={e => setParameterExecution(prev => ({...prev, [key]: e.target.checked ? 1 : 0}))} />
                  {label}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">Enable the module call type, then opt this parameter in. Disabled parameters return None; existing calculation rules still apply.</p>
          </div>

        </div>}
        {/* LLM-based fields */}
        {subparameterKind !== 'logic_based' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Definition
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md global-input"
                rows="3"
                value={subparameterDefinition}
                onChange={(e) => setSubparameterDefinition(e.target.value)}
              />
            </div>
          </>
        )}

        {/* Logic-based fields */}
        {subparameterKind === 'logic_based' && (
          <>
            {/* Subparameters Used picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subparameters Used</label>
              <div className="flex gap-2 mb-2 relative">
                <input
                  ref={subparamVarInputRef}
                  type="text"
                  value={subparamSelectedVar}
                  onChange={(e) => { setSubparamSelectedVar(e.target.value); setSubparamShowVarDropdown(true); }}
                  onFocus={() => setSubparamShowVarDropdown(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVarToSubparam(); } }}
                  className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                  placeholder="Enter variable name"
                />
                <button
                  onClick={() => addVarToSubparam()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add
                </button>
                {subparamShowVarDropdown && filteredSubparamVars.length > 0 && (
                  <div
                    ref={subparamVarDropdownRef}
                    className="absolute top-full left-0 right-24 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
                  >
                    {filteredSubparamVars.map((name) => (
                      <div
                        key={name}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => { setSubparamSelectedVar(name); setSubparamShowVarDropdown(false); }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {subparamSelectedVars.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {subparamSelectedVars.filter(n => !n.startsWith('prev_')).length > 0 && (
                    <>
                      <div className="w-full text-xs font-medium text-gray-700 mb-1">Variables:</div>
                      {subparamSelectedVars.filter(n => !n.startsWith('prev_')).map((name) => (
                        <div key={name} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                          <span className="text-sm text-gray-700">{name}</span>
                          <button onClick={() => removeVarFromSubparam(name)} className="text-gray-500 hover:text-gray-700">
                            <FaTimes style={{color: "slategray"}}/>
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                  {subparamSelectedVars.filter(n => n.startsWith('prev_')).length > 0 && (
                    <>
                      <div className="w-full text-xs font-medium text-gray-700 mb-1 mt-1">Previous values:</div>
                      {subparamSelectedVars.filter(n => n.startsWith('prev_')).map((name) => (
                        <div key={name} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                          <span className="text-sm text-gray-700">{name}</span>
                          <button onClick={() => removeVarFromSubparam(name)} className="text-gray-500 hover:text-gray-700">
                            <FaTimes style={{color: "slategray"}}/>
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Output Type - shown for both */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Output Type
          </label>
          <ModernTimeDropdown width='w-full' isDisabled={editingParameter?.id} placeholder="Select Type" value={subparameterType === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : subparameterType} options={classifierOutputOptions(subparameterKind, ['Yes/No/NA', 'Categorical - Single Value', 'Categorical - Multiple Values', 'Number', 'Text', 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)'])}
            onChange={(e) => {
              const label = 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)';
              setSubparameterType(e.target.value === label ? 'Date and Time' : e.target.value)
            }} />
        </div>

        {/* Options for categorical types */}
        {(subparameterType === 'Categorical - Single Value' || subparameterType === 'Categorical - Multiple Values') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md global-input"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="Type and press Enter to add"
              />
              <button
                onClick={handleAddTag}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {subparameterPossibleValues.map((tag, index) => (
                <div key={index} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                  <span className="text-sm text-gray-700">{tag}</span>
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logic editor - only for logic_based */}
        {subparameterKind === 'logic_based' && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <label className="block text-sm font-medium text-gray-700">Logic</label>
                <div className="relative">
                  <Info size={16} className="text-gray-400" />
                </div>
              </div>
              <button
                onClick={() => verifySubparamLogic()}
                className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300" style={{height: "32px", borderRadius: "4px"}}
              >
                Validate Logic
              </button>
            </div>
            {!subparamLogicLoading && (
              <div className="mb-2">
                <div className='flex items-center gap-2 mt-2'>
                  <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "60%"}}>Output can be text or number</p>
                  <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "40%"}}>Explanation</p>
                </div>
                <div className='flex items-start gap-2'>
                  <textarea
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    rows="6"
                    placeholder="Enter your logic here..."
                    value={subparamLogic}
                    onChange={(e) => setSubparamLogic(e.target.value)}
                    style={{border: subparamLogicError ? "1px solid red" : "", width: "60%", height: "180px", overflow: "auto", fontSize: "14px"}}
                  />
                  <textarea
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={subparamLogicExplanation}
                    onChange={(e) => setSubparamLogicExplanation(e.target.value)}
                    rows="6"
                    style={{ width: "40%", height: "180px", overflow: "auto", fontSize: "14px", whiteSpace: "pre-line"}}
                  />
                </div>
                {subparamLogicError && (<p className="mt-2 text-sm text-red-600">{subparamLogicError}</p>)}
              </div>
            )}
            {subparamLogicLoading && (
              <div className="flex justify-center items-center my-4" style={{ flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div>
            )}
          </div>
        )}
      </div>}
      {
        showLoadConfigurePart && (
          <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>
        )
      }

      <div className="p-4 border-t flex justify-end space-x-3">
        <button
          onClick={() => {setSubparameterName('');
            setSubparameterDefinition('');
            setContextScope('conversation');
            setParameterExecution({});
            setSubparameterType('Yes/No/NA');
            setSubparameterPossibleValues([]);
            setSubparameterTags([]);
            setEditingParameter(null);
            setShowSubparameterModal(false);
            setSubparameterKind('llm_based');
            setClassifierCriteria({});
            setClassifierScoreMax(undefined);
            setSubparamLogic('');
            setSubparamLogicExplanation('');
            setSubparamLogicError('');
            setSubparamSelectedVars([]);
            setSubparamSelectedVar('');}}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
        >
          Cancel
        </button>
        <button
          onClick={handleSaveSubparameter}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed" style={{height: "40px", borderRadius: "4px"}}
          disabled={showLoadConfigurePart}
        >
          Save
        </button>
      </div>
    </div>
  </div>
)}
                </div>
              </div>
            )}

            {activeTab === 'parameters' && (
              <div className="p-6">
                <div className="mb-4">
                  <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'><h2 className="text-lg font-medium text-gray-800 mb-3">Parameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search parameters by name" value={tableSearch.parameters} onChange={value => setTableSearch({ ...tableSearch, parameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                      {/* Show Archived Parameters Checkbox */}
                      <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={showArchivedParameters}
                          onChange={(e) => setShowArchivedParameters(e.target.checked)}
                          className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show Archived Parameters
                      </label>
                      <button
                        className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                        onClick={handleOpenParameterForm}
                      >
                        Add Parameter
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="configuration-table divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "30%" }} />
                        <col />
                        <col style={{ width: "80px" }} />
                        <col style={{ width: "160px" }} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logic</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subparameters Used</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parameterTableRows(getFilteredParameters(), tableSearch.parameters).map(param => (
                          <tr key={param.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{param.display_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                              <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">
                                {param.logic.length>300 ? param.logic.slice(0, 300)+"..." : param.logic}
                              </code>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words" style={{whiteSpace: "normal", wordBreak: "break-word", width: "24%"}}>{param.subparameters_used.join(', ')}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                {/* Toggle Switch for Active/Inactive (disabled for archived) */}
                                {param.status !== 'archived' && (
                                  <>
                                    <input 
                                      type="checkbox" 
                                      checked={!param.status || param.status === 'active'} 
                                      onChange={() => handleToggleParameterStatus(param)}
                                      className="sr-only global-input"
                                      id={`toggle-param-status-${param.id}`}
                                    />
                                    <label 
                                      htmlFor={`toggle-param-status-${param.id}`}
                                      className={`block h-5 w-12 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                                        !param.status || param.status === 'active' ? 'bg-indigo-600' : 'bg-gray-200'
                                      }`}
                                    >
                                      <span 
                                        className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${
                                          !param.status || param.status === 'active' ? 'translate-x-7' : 'translate-x-0'
                                        }`}
                                      />
                                    </label>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {param.status === 'archived' ? (
                                // Show restore button for archived parameters
                                <CustomTooltip text="Restore parameter" id={`restore-param-${param.id}`} position="left">
                                  <button
                                    onClick={() => handleRestoreParameter(param)}
                                    className="hover:text-green-600 mr-4"
                                  >
                                    <TbRestore className='h-4 w-4'/>
                                  </button>
                                </CustomTooltip>
                              ) : (
                                // Show edit and archive buttons for active/inactive parameters
                                <div className='flex items-center justify-end'>
                                  <CustomTooltip text="Edit parameter" id={`edit-param-${param.id}`} position="left">
                                    <button 
                                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                                      onClick={() => handleEditParameter(param.id)}
                                    >
                                      <FaRegEdit/>
                                    </button>
                                  </CustomTooltip>
                                  <CustomTooltip text="Archive parameter" id={`archive-param-${param.id}`} position="left">
                                    <button
                                      onClick={() => {setShowArchiveModal(true); setArchiveItem(param); setArchiveType('parameter');}}
                                      className="text-red-600 hover:text-red-900 mr-4"
                                    >
                                      <Archive className='h-4 w-4'/>
                                    </button>
                                  </CustomTooltip>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div></div>
                  {showParameterForm && (
          <div ref={parameterFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" style={{width: "85%", margin: "auto", marginTop: "2rem"}}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                {isEdit ? "Edit Parameter" : "Add Parameter"}
                {isEdit && <div className="ml-1 relative">
                  <div className="group">
                    <Info className="h-4 w-4 text-gray-500 cursor-help ml-2" />
                    <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                      Name & Output type can't be modified.
                    </div>
                  </div>
                </div>}
              </h3>
              <button 
                onClick={() => {setShowParameterForm(false); setLogicLoading(false);}}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>

            {/* Parameter Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parameter Name
              </label>
              <input
                className="w-full px-4 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                type="text"
                placeholder="Enter Parameter Name"
                value={parameter_name}
                disabled={isEdit}
                onChange={(e) => handleChangeParams(e, 'parameter')}
              />
              <div style={{display: "flex", justifyContent: "left", color: "red", fontSize: "12px"}}>{error}</div>
            </div>

            {/* Subparameters Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subparameters Used
        </label>
        <div className="flex gap-2 mb-2 relative">
          <input class="global-input"
            ref={inputRef}
            type="text" 
            value={selectedSubparameter} 
            onChange={(e) => {
              setSelectedSubparameter(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
            placeholder="Enter subparameter name"
          />
          <div className="absolute inset-y-0 right-20 flex items-center cursor-pointer">
      {showDropdown ? (
        // Up arrow when open
        <svg
          className="w-4 h-4 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      ) : (
        // Down arrow when closed
        <svg
          className="w-4 h-4 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      )}
    </div>
          <button
            onClick={handleAddSubparameterToQuery}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Add
          </button>
          
          {/* Dropdown for subparameter suggestions */}
          {showDropdown && filteredSubparameters.length > 0 && (
            <div 
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
            >
              {filteredSubparameters.map((subparam) => (
                <div
                  key={subparam}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectSubparameter(subparam)}
                >
                  {subparam}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          {selectedSubparameters.map((subparam) => (
            <div
              key={subparam}
              className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full"
            >
              <span className="text-sm text-gray-700">{subparam}</span>
              <button
                onClick={() => handleRemoveSubparameterFromQuery(subparam)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>
          ))}
          {
            usingSubParamError && (<div style={{color: "red", fontSize: "12px"}}>{usingSubParamError}</div>)
          }
        </div>
      </div>

            {/* Logic Section */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Logic
                  </label>
                  <div className="relative">
                    <Info 
                      size={16} 
                      className="text-gray-400 hover:text-gray-600 cursor-pointer"
                      onClick={() => setShowLogicInfo(!showLogicInfo)}
                    />
                    {showLogicInfo && (
                      <div className="absolute z-50 left-6 -top-2 w-[700px] h-[40vh] overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex justify-between items-center">
                          <h4 className="font-medium text-gray-800">Write Scoring Logic (Python-style)</h4>
                          <button
                            onClick={() => setShowLogicInfo(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <FaTimes style={{color: "slategray"}}/>
                          </button>
                          </div>
                          <p className="text-sm text-gray-600">Use simple if, elif, and else statements to define logic.</p>
                          <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                            <li>Only use subparameters you have added, with the same spelling.</li>
                            <li>Return numeric values only (between 0 and 100).</li>
                            <li>Supports and, or, in, not in, math expressions, and nesting.</li>
                          </ul>
                          <div className="mt-2">
                            <p className="text-sm font-medium text-gray-700 mb-1">Examples:</p>
                            <pre className="bg-gray-50 p-2 rounded text-sm">
                              {`Example 1 - 
if greeting == "Yes" and brand_name_mention == "Yes":
    return 100
else:
    return 0

Example 2 -
if customer_sentiment == "Very Positive":
    return 100
elif customer_sentiment == "Positive":
    return 80
elif greeting == "Yes" and brand_name_mention == "Yes":
    return 60
else:
    return 0

Example 3 - 
if (1==1):
    return (0.5 * agent_engagement_score + 0.5 * customer_satisfaction_score)`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => verifyQuery()}
                  className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300" style={{height: "40px", borderRadius: "4px"}}
                >
                  Validate Logic
                </button>
              </div>
            <div className='p-3 border rounded-lg my-2 mb-4 bg-indigo-50 border-indigo-200'>
              <div>
                <div className='flex items-center justify-between h-6 cursor-pointer' onClick={() => setWriteWithAiEnabled(!writeWithAiEnabled)}>
                <div className="text-sm font-medium flex items-center text-indigo-600">
                 <Sparkles className='h-4 w-4 mr-2 text-indigo-600'/> Write with AI
                </div>
                <div className='flex items-center gap-2 cursor-pointer'>
                {writeWithAiEnabled ? (
                  // Up arrow when open
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  // Down arrow when closed
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
                </div>
                </div>
              {writeWithAiEnabled && <div className='mt-2'>
                <div>
                  <textarea
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    rows="6"
                    placeholder="Describe the logic you want to implement using the subparameters above…"
                    value={promptQuery}
                    onChange={(e) => setPromptQuery(e.target.value)} style={{fontSize: "14px"}}
                  />
                </div>
                <div className='flex justify-end'>
                  <button
                    onClick={generateLogicWithAI}
                    className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center text-sm" style={{height: "35px", borderRadius: "4px"}}
                  >
                    <Wand2 className='h-3 w-3 mr-2'/> Generate
                  </button>
                </div>
                </div>}
              </div>
            </div>
            {!logicLoading && <div className="mb-6">
              <div className='flex items-center gap-2 mt-2'>
              <p style={{fontSize: "12px", fontStyle: "italic", color: "gray",width: "60%"}}>Score should be between 0 to 100</p>
              <p style={{fontSize: "12px", fontStyle: "italic", color: "gray",width: "40%"}}>Explanation</p>
              </div>
              <div className='flex items-start gap-2'>
              <textarea
                className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                rows="6"
                placeholder="Enter your logic here..."
                value={manualQuery}
                onChange={(e) => handleQueryChange(e.target.value)} 
                style={{border: queryError ? "1px solid red" : "", width: "60%", height: "200px", overflow: "auto", fontSize: "14px"}}
              />
                <textarea
                  className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                  value={logicExplanation} rows="7"
                  style={{width: "40%", fontSize: "14px", height: "200px", overflow: "auto", whiteSpace: "pre-line"}}
                />
              </div>
              {queryError && (
                <p className="mt-2 text-sm text-red-600">{queryError}</p>
              )}
            </div>}
            {
              logicLoading && (
                <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                  <div className="loader"></div>
                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                </div>
              )
            }

            {/* Form Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {setShowParameterForm(false); setQueryError(''); setManualQuery(''); setWriteWithAiEnabled(false); setPromptQuery(''); setEditingParameterId(null); setLogicLoading(false);}}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
              >
                Cancel
              </button>
              <button
                onClick={handleSave} disabled={!isValid || manualQuery=='' || queryError!==''}
                className={`px-4 py-2  text-white rounded-lg ${(!isValid || manualQuery=='' || queryError!=='') ? 'bg-gray-500 hover:bg-gray-700' : 'bg-indigo-600 hover:bg-indigo-700'} `} style={{height: "40px", borderRadius: "4px"}}
              >
                Save
              </button>
            </div>
          </div>
        )}
                </div>
              </div>
            )}
          </div>}
          {
            loading && (
              <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div>
            )
          }
        </div>
        {
          showSampleTestModal && (
            <div>
              <div style={{position: "fixed", left: "2%", top: "6.5%", width: "96%", height: "90%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                zIndex: "999999999", padding: "0 15px"}}>
                <SampleTestModal clientID={client_id} serviceID={service_id} emailID={email} moduleName='agent_feedback' onClose={handleCloseSampleTestModal}/>
              </div>
            </div>
          )
        }
        {
          showArchiveModal && (
            <div style={{zIndex: "9999999999", position: "fixed", top: "0", left: "0", height: "100%", width: "100%", background: "rgba(0,0,0, 0.5)"}}>
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                <div className="flex justify-between items-center p-4 pb-2">
                  <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Are you sure?</h2>
                  <button 
                    onClick={() => {
                      {setShowArchiveModal(false); setArchiveItem(null); setArchiveType(null);}
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                  <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
                
                <div className="p-6 pt-2">
                  <p className="text-gray-500 text-sm text-left">
                    This {archiveType} will be archived.
                    {archiveType === 'parameter' && ' Make sure its weightage is set to 0 and removed from fatal condition before archiving.'}
                  </p>
                  {archiveType === 'subparameter' && archiveItem && (() => {
                    const { usedByParameters, usedByLogicSubparams } = getSubparameterArchiveBlockers(archiveItem);
                    return (
                      <div className="mt-4 space-y-3 text-sm text-left text-gray-700">
                        <div>
                          <p className="font-medium text-gray-800">Parameters using this subparameter</p>
                          {usedByParameters.length === 0 ? (
                            <p className="text-gray-500">None</p>
                          ) : (
                            <ul className="mt-1 list-disc list-inside">
                              {usedByParameters.map((p) => (
                                <li key={p.id}>{p.display_name}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            Logic-based subparameters using this subparameter
                          </p>
                          {usedByLogicSubparams.length === 0 ? (
                            <p className="text-gray-500">None</p>
                          ) : (
                            <ul className="mt-1 list-disc list-inside">
                              {usedByLogicSubparams.map((s) => (
                                <li key={s.id}>{s.display_name}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                <div className="flex justify-end gap-3 p-6 pt-6">
                  <button
                    onClick={() => {setShowArchiveModal(false); setArchiveItem(null); setArchiveType(null);}}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
                  >
                    No
                  </button>
                  <button onClick={() => {
                    if (archiveType === 'parameter') {
                      handleArchiveParameter(archiveItem);
                    } else if (archiveType === 'subparameter') {
                      handleArchiveSubparameter(archiveItem);
                    }
                    setShowArchiveModal(false);
                    setArchiveItem(null);
                    setArchiveType(null);
                  }}
                    className={`px-6 py-2 rounded-lg ${
                      archiveType === 'subparameter' &&
                      archiveItem &&
                      (() => {
                        const b = getSubparameterArchiveBlockers(archiveItem);
                        return b.usedByParameters.length + b.usedByLogicSubparams.length > 0;
                      })()
                        ? 'bg-gray-500 hover:bg-gray-600'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    } text-white`} style={{height: "40px", borderRadius: "4px"}}
                    disabled={
                      archiveType === 'subparameter' &&
                      archiveItem &&
                      (() => {
                        const b = getSubparameterArchiveBlockers(archiveItem);
                        return b.usedByParameters.length + b.usedByLogicSubparams.length > 0;
                      })()
                    }
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
        </div>
          )
        }
      </div>
    );
  }
  
  // Preview Screen
  return (
    <div className="p-3">
      <div className=" mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Agent Feedback</h1>
            <p className="text-gray-500 text-sm mt-1">Sample results</p>
          </div>
          <div className="flex space-x-3">
            <button 
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none"style={{height: "40px", borderRadius: "4px"}}
              onClick={() => {setShowPreview(false); setExpandedSample(null); setSamples([]);}}
            >
              Go Back
            </button>
            <button onClick={() => {generateSamples(0); setSamples([]); setExpandedSample(null);}} className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none"style={{height: "40px", borderRadius: "4px"}}>
              Run Different Sample
            </button>
            <ConfigurationDiffButton
              title="Agent Feedback Changes"
              originalConfig={() => configWithExecutionSettings(agentFeedbackModule, loadedModuleExecution)}
              modifiedConfig={() => configWithExecutionSettings(getCurrentConfigForDiff(), moduleExecution)}
              triggerLabel="Deploy"
              showTriggerIcon={false}
              disabled={loading}
              triggerClassName={`px-4 py-1 h-10 ${loading ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}  text-white rounded  focus:outline-none`}
              onNext={() => setShowDeployConfirmation(true)}
            />
          </div>
        </div>
        <DeployConfirmationModal
          isOpen={showDeployConfirmation}
          confirmationText={`Changes will be deployed in ${client_id || 'N/A'}, ${service_id || 'N/A'} and ${module_name || 'agent_feedback'}`}
          onClose={() => setShowDeployConfirmation(false)}
          onConfirm={(versionDescription) => {
            setShowDeployConfirmation(false);
            deployModule(versionDescription);
          }}
        />

        <div style={{height: "75vh", overflow: "auto"}}>
        {!loading && Array.isArray(samples) && samples.map((sample, index) => (
          <div key={sample.call_id} className="border-b border-gray-200 last:border-0 my-2">
            <div  onClick={() => setExpandedSample(expandedSample === index ? null : index)} className='p-4 border-b rounded-lg border-gray-200 bg-gray-50 cursor-pointer'>
            <div 
              className="flex justify-between items-center"
             
            >
              <div className="flex items-center">
                <span className="font-medium text-gray-800">{sample.name}</span>
              </div>
              <svg 
                className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSample === index ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            <div className="text-sm text-gray-600 mt-1 capitalize" style={{fontSize: "12px"}}>
                        ID: {sample.call_id} | Time: {sample.call_time} | Employee: {sample.employee_name} | Record Type: {sample.record_type}
                      </div>
            </div>
            {expandedSample === index && (
              <div className="bg-white rounded-lg shadow p-4 overflow-hidden border flex">
                <div className="border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.65"}}>
                            <div 
                              className="p-2 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center" style={{justifyContent: "space-between"}}
                            >
                              
                              <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                                </svg>
                                <h3 className="font-medium text-gray-800">Output</h3>
                              </div>

                            </div>
                            <div className="p-4">
                  {sample.parameters &&
                  <FeedbackSection key="parameters" showMoreMap={showMoreMap} toggleShowMore={toggleShowMore} source_click={(e)=>handleSourceClick(e, sample.segments)}
                  dataKey="Agent Feedback" metadata={sample.parameters?.metadata}
                  data={sample.parameters?.parameters} maxHeight="45vh" />}
                </div>
                </div>
                {/* Audio Section */}
                <div className="border border-gray-200 rounded-lg overflow-hidden ml-2" style={{flex: "0.35"}}>
                            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center">
                              <div className="flex items-center">
                              {sample.record_type === 'call' ? (
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                </svg>
                  ) : (<IoChatbubbleOutline style={{marginRight: "0.5rem", height: "30px"}}/>)}
                                <h3 className="font-medium text-gray-800">{sample.record_type === 'call' ? 'Audio' : 'Chat' }</h3>
                              </div>
                            </div>
                            {sample.record_type === 'call' && <div className="p-2 bg-white">
                              <audio controls key={sample.record_link} ref={audioRef} onTimeUpdate={()=>handleAudioTimeUpdate(sample.segments)} style={{ width: "96%", margin: "0 1rem", height: "40px" }}>
                                <source src={sample.record_link} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>}
                            <hr/>
                            <div style={{fontSize: "13px", margin: "10px", overflow: 'auto', height: "40vh"}}>
                            {
                              sample.record_type === 'call' && sample.segments.map((item, idx)=> {
                                        const isSelected = selectedSegmentIdx === idx;
                                        return (
                                          <div key={`segment_${idx}`} ref={(el) => (segmentRefs.current[idx] = el)} onClick={()=>toggleSegment(item, idx, sample.record_link)} style={{marginBottom: "10px", borderLeft: isSelected ? "4px solid #6f6ffe":"", background: isSelected ? "rgba(249, 250, 251, 1)":"",
                                            padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                              <div style={{fontSize: "14px", color: "grey"}}>{item.speaker}</div>
                                              <div style={{fontSize: "14px", color: "grey"}}>{formatTime(item.start_time)}</div>
                                            </div>
                                            <div style={{fontSize: "15px"}}>{item.text}</div>
                                          </div>
                                        )
                                      })
                                    }
                            {
                              sample.record_type === 'chat' && sample.segments.map((item, idx)=> {
                                return (
                                  <div key={`segment_${idx}`}
                                  data-message-id={item.message_id}
                                  style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                                    padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                      <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                                        <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                                        {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                                      </div>
                                      <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                                    </div>
                                    <div style={{fontSize: "14px"}}><span style={{whiteSpace: "pre-line"}}>{item.content?.text?.content} <br/></span></div>
                                    {item.content.image && item.content.image.url && 
                                      <div style={{fontSize: "15px", whiteSpace: "pre-line"}}>
                                        <br/>
                                       Image Shared - {item.content.image.url}
                                      </div>
                                      }
                                    {
                                      item.content.buttons && item.content.buttons.length > 0 && (
                                        <div>
                                          <br/>
                                          {item.actor_type === 'user' ? (
                                            <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          ) : (
                                            <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          )}
                                        </div>
                                      )
                                    }
                                  </div>
                                )
                                      })
                                    }
                            </div>
                          </div>
                          
              </div>
            )}
          </div>
        ))}
        {
          loading && (<div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>)
        }
        </div>
      </div>
    </div>
  );
};

export default AgentFeedbackConfigure;
